import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, useContext, useId } from 'react';
import { flushSync } from 'react-dom';
import { getChat, saveChatWithMessages } from '../firebase/db';
import { renderMarkdown, renderAnswer } from '../utils/renderMarkdown';
import { withLeadIn, formatSourceDate, sourcesHeading } from '../utils/sermonTimestampLinks';
import { mapChatError } from '../utils/userFacingErrors';
import { authedFetch, newChatId, aiRenoticeDue, AI_RENOTICE_TEXT, PUBLIC_SITE_URL, ADMIN_SITE_URL } from '../utils/api';
import { TeamRoleContext } from './TeamRoleContext';
import { streamChat } from '../utils/chatStream';
import { buildHistory, maybeCompact } from '../utils/compaction';
import { SpeechPlayer, startRecording, transcribe, micSupported } from '../utils/voice';
import { useVoiceReplies, subscribeVoiceReplies, setVoiceReplies as setVoicePreference, voiceSrc } from '../utils/voicePreference';
import { useTeamVoice, teamVoicePick } from '../utils/teamVoice';
import { sampleStarters, nextStarters, nextStepOrder, logArrivalEvent, conversationContext } from '../utils/campaign';
import { useStarterGlimmer, GLIMMER } from '../utils/starterGlimmer';
import { trackAsk, trackGoal, trackScripture, trackVoice, onLeaving, isInternalBrowser } from '../utils/analytics';
import {
  frontDoorFor, noteFrontDoorShown, noteOwnQuestion, firstAnswerFields, readFirstAnswer, enrollFirstAnswer, noteFirstAnswer, watchFirstAnswerLeaving,
} from '../utils/experiments';
import { trackFirstQuestion } from '../utils/metaPixel';
import { handoffSwitches, anyHandoffOn } from '../utils/humanSteps';
import { robVideoFor } from '../utils/robVideo';
import { expectedLatency, recordLatencySample, formatDuration } from '../utils/latency';
import {
  usePublicConfig, applyPublicConfig, isSupportTurn, isDonationTurn, markDonationCardHandled, costSentence, SUPPORT_LEAD,
  sheetOn, openGiveFromClick, useGivingState, getGivingState, holdGiving, restGiving, reportConversation, forgetConversation,
  readGiveOut, clearGiveOut, noteDeliberateLeave, scriptureModeNow,
} from '../utils/giving';
import { restUntilWindowEnd, returnNoteDue, COPY as GIVE_COPY, TRACK_OPEN, inAppBrowser, offersHelp } from '../utils/giveCore';
import { underEighteenIn, abuseCameUpIn } from '../utils/askRob';
import { scriptureUrl, isSelectionGesture } from '../utils/scripture';
import { JOINED, PROMISE } from '../utils/emailList';
import { placeCrisisCards, crisisTurns } from '../utils/crisisCards';
import { reportError } from '../monitoring';
import CrisisResources from './CrisisResources';
import ThinkingCard from './ThinkingCard';
import AskRobPanel from './AskRobPanel';
import EmailSignup from './EmailSignup';
import GeniusReader from './GeniusReader';
import { BOOK_AMAZON_URL, THE_NET_YOUTUBE_URL, askAboutPassage, chapterFromTitle, quoteNear, quoteFromAnswer, verifiedQuoteFor } from '../utils/book';
import Icon from './Icon';
import { AiLabel, UserTurn, WelcomeTitle, StarterRows, NextSteps, HumanDoor, RobVideoCard, VoiceBar, VoiceLevel, SupportLine, CrisisLine, AskOffer, QuoteChip, MoreAnswer, sourceSummary } from './ChatParts';
import { cleanQuote, defaultAsk, foldForPeople, askAboutOn, quoteFromSelection } from '../utils/quote';
import { typedThisVisit, markTyped } from '../utils/typeOn';
import { approvedAnswerFor } from '../utils/canonical';
import { ShellChrome } from './ShellChrome';
import './ChatView.css';

// ---------------------------------------------------------------------------
// Device-local draft (archive mode). Nothing leaves the browser — this is the
// same privacy class as the words already on screen. TTL keeps a stale draft
// from resurrecting days later.
// ---------------------------------------------------------------------------
const DRAFT_TTL_MS = 12 * 60 * 60 * 1000;

function readDraft(key) {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const draft = JSON.parse(raw);
    if (!Array.isArray(draft.messages) || !draft.messages.length) return null;
    if (!draft.t || Date.now() - draft.t > DRAFT_TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return draft;
  } catch (e) {
    return null;
  }
}

function writeDraft(key, messages, chatId) {
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify({ messages, chatId, t: Date.now() }));
  } catch (e) { /* storage full/blocked — the draft is best-effort */ }
}

function clearDraft(key) {
  if (!key) return;
  try { localStorage.removeItem(key); } catch (e) { /* ignore */ }
}

const TIER_KEY = 'apr-answer-tier';
const GUEST_NUDGE_KEY = 'apr-guest-nudge-shown';

// Phones: after this long with no touch, or on a scroll down into the
// conversation, the tier/voice/save row and the disclaimer fade out and the
// input row drops to the bottom edge. Focusing or typing brings them back.
const CHROME_IDLE_MS = 5000;
// "Phone" in either orientation: narrow, or short (a landscape phone is
// ~390px tall, which is exactly where reading room matters most).
const COMPACT_QUERY = '(max-width: 720px), (max-height: 500px)';
const TUCK_DOWN_PX = 12; // a deliberate drag down tucks the chrome away
const UNTUCK_UP_PX = 36; // a deliberate drag up brings the top bar back
const CHROME_SETTLE_MS = 360; // the 0.28s slide plus a margin
// Never tuck a conversation that would then fit on screen: the bar would
// slide away, the list would stop scrolling, and the bar would slide back.
const TUCK_MIN_OVERFLOW = 240;
const ANCHOR_GAP = 12; // room above a question parked at the top of the screen
const FADE_MS = 200; // how long newly streamed words take to ink in (--stream-word-fade, BRAND_SPEC §9.5)
const CARET_BLINK_MS = 4240; // the welcome caret's four blinks (--caret-blink-max, BRAND_SPEC §9.2)
// The default welcome's first view of a visit (Alexi, Oct 17, v2: "when it
// loads the ask anything should be first with the input box, 100 percent").
// The first frame is "Ask anything." typing and the box under it, nothing
// else: the menu, the questions, the fine print and the AI notice wait, in
// their places (opacity only), each coming in on its own, one thing moving at
// a time. The times are from the first frame (the typing's start):
//   ~1.5 s  the caret drops into the box; the line under the box comes in
//   4.0 s   the four questions, one by one
//   5.4 s   the menu (the desktop's rail, the phone's menu button) and More questions
//   5.8 s   Ask glimmers, twice
//   7.1 s   the AI notice (its crisis link) and the fine print, slowly: in by 8.5 s
// then the caret's four blinks, then the questions' glimmer. The instant
// someone starts (a key, a paste, a tap or click anywhere, a scroll, the mic,
// a question, Tab), everything comes in at once, the AI notice first of all,
// and nothing is ever sent while it is hidden (sendMessage).
const REVEAL = {
  lineMs: 300, // the line under the box fades in as the caret drops into it
  questionsAt: 4000, // the first question starts to come in
  questionMs: 350, // each question's fade; the next one starts as it ends
  menuMs: 400, // the menu and More questions, after the last question
  glimmerMs: 650, // one of Ask's two sweeps of light, after the menu
  noticeMs: 1400, // the AI notice and the fine print, slowly
};
// The AI notice and the fine print start to come in no earlier than this (ms
// from the first frame), unless someone starts first. Decided by Alexi, Oct 17:
// 7.1 s, as built. To bring the notice in sooner, change this one number:
// 4000 brings it in with the questions (then two things move at once); 0
// keeps it, and the fine print, on screen from the first frame, as before the
// reveal. scripts/phone/welcome.js (AT) checks these times: a change here is
// a change there.
const AI_NOTICE_NOT_BEFORE_MS = 7100;
const NOTICE_STAGED = AI_NOTICE_NOT_BEFORE_MS > 0; // 0: the notice is never held back
// When each part may start to come in, and when it is all over.
const REVEAL_QUESTIONS_AT = [0, 1, 2, 3].map((i) => REVEAL.questionsAt + i * REVEAL.questionMs);
const REVEAL_MENU_AT = REVEAL.questionsAt + 4 * REVEAL.questionMs;
const REVEAL_GLIMMER_AT = REVEAL_MENU_AT + REVEAL.menuMs;
const REVEAL_END_AT = Math.max(REVEAL_GLIMMER_AT + 2 * REVEAL.glimmerMs, NOTICE_STAGED ? AI_NOTICE_NOT_BEFORE_MS + REVEAL.noticeMs : 0);
const REVEAL_MARKS = [...new Set([...REVEAL_QUESTIONS_AT, REVEAL_MENU_AT, REVEAL_GLIMMER_AT, ...(NOTICE_STAGED ? [AI_NOTICE_NOT_BEFORE_MS] : []), REVEAL_END_AT])]
  .sort((a, b) => a - b);
// The one place the fade lengths are written: ChatView.css reads them. They go
// on the page's root while the reveal plays, so the menu and the AI notice
// (in the app's shell, outside this view) read the same ones.
const REVEAL_STYLE = {
  '--reveal-line': `${REVEAL.lineMs}ms`,
  '--reveal-question': `${REVEAL.questionMs}ms`,
  '--reveal-menu': `${REVEAL.menuMs}ms`,
  '--reveal-glimmer': `${REVEAL.glimmerMs}ms`,
  '--reveal-notice': `${REVEAL.noticeMs}ms`,
};
// If the typing never starts while the page is in front of someone, everything
// comes in after this long (the typing itself is done by about 1.5 s).
const REVEAL_FALLBACK_MS = 2500;
// "Ask about this" (utils/quote.js): on a touch screen a selection is offered
// once it has held still this long (its handles are moving until then), and
// the offer stays this long after the selection goes, so a tap that clears
// the selection on its way to the button still lands. What a screen reader
// hears, once each.
const ASK_SETTLE_MS = 300;
const ASK_LINGER_MS = 400;
const ASK_OFFERED = 'Ask about this is ready, just above the message box.';
const QUOTE_ADDED = 'Quote added above the message box. Ask about it, or just press Ask.';
const QUOTE_REMOVED = 'Quote removed.';
// What the box says beside a quote, and the first half alone where the whole
// doesn't fit (the narrowest phones, or a phone's text made larger).
const QUOTE_HINT = 'Ask about it, or just tap Ask';
const QUOTE_HINT_SHORT = 'Ask about it';
let hintCanvas = null;

/**
 * Whether `text` fits on one line as the box's placeholder: measured in the
 * placeholder's own font (Newsreader italic, ChatView.css) against the box's
 * room for words, so the narrowest phones and larger text both get the
 * short line, never one cut off mid-word. A browser that can't say what the
 * placeholder's font is gives the box's own (Inter, wider): the short line
 * then, never a clipped one.
 */
function hintFits(box, text) {
  try {
    const own = window.getComputedStyle(box);
    const hint = window.getComputedStyle(box, '::placeholder');
    const font = hint && hint.fontFamily ? hint : own;
    hintCanvas = hintCanvas || document.createElement('canvas');
    const ctx = hintCanvas.getContext('2d');
    ctx.font = `${font.fontStyle} ${font.fontWeight} ${font.fontSize} ${font.fontFamily}`;
    const room = box.clientWidth - parseFloat(own.paddingLeft) - parseFloat(own.paddingRight);
    return room > 0 && ctx.measureText(text).width <= room;
  } catch (e) {
    return false;
  }
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && Boolean(window.matchMedia) &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;
// A high-contrast mode (Windows High Contrast and the like) shows "Ask
// anything." whole and still, never typed (ChatView.css hides the typed copy
// there), so the first view isn't staged there either: nothing would move
// while the rest waited.
const forcedColors = () =>
  typeof window !== 'undefined' && Boolean(window.matchMedia) &&
  window.matchMedia('(forced-colors: active)').matches;
/** How much of an element shows: its opacity times that of everything around it (the first view's reveal). */
function shownOpacity(el) {
  let o = 1;
  for (let n = el; n && n.nodeType === 1; n = n.parentElement) o *= parseFloat(getComputedStyle(n).opacity);
  return o;
}
/** True while an element, or anything around it, is not yet fully in. */
const seeThrough = (el) => shownOpacity(el) < 0.99;
// What a tap can land on, and how faint a part held back by the first view
// still is when a press on it is a press on nothing (ChatView's reveal).
const TAP_TARGETS = 'a[href], button, [role="button"]';
const UNSEEN_OPACITY = 0.1;
/**
 * The AI notice as the browser draws it: true when every bar on the page is
 * fully in. With `force`, a bar still drawn see-through once the first view's
 * reveal is over (a browser whose :has() never caught up with the view's
 * classes) is made fully visible, so nothing is ever asked behind it.
 */
function noticeIn({ force = false } = {}) {
  if (typeof document === 'undefined') return true;
  let shown = true;
  document.querySelectorAll('.ai-disclosure').forEach((el) => {
    if (!seeThrough(el)) return;
    shown = false;
    if (force) el.style.setProperty('opacity', '1', 'important');
  });
  return shown;
}

/**
 * Streamed words ink in instead of popping. The markdown body is re-rendered
 * (innerHTML) on every chunk, so each render re-wraps the text that arrived
 * in the last FADE_MS in spans whose negative animation-delay continues the
 * fade exactly where the previous render left it. Offsets are positions in
 * the rendered text (textContent), which only ever grows while streaming.
 */
function inkFreshText(root, spans, now) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes = [];
  let pos = 0;
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    nodes.push([n, pos]);
    pos += n.data.length;
  }
  nodes.forEach(([node, start]) => {
    const end = start + node.data.length;
    if (!/\S/.test(node.data)) return; // whitespace between blocks
    const hits = spans.filter((s) => s.to > start && s.from < end);
    // Right to left, so `node` keeps the untouched prefix for the next hit.
    for (let i = hits.length - 1; i >= 0; i--) {
      const a = Math.max(hits[i].from, start) - start;
      const b = Math.min(hits[i].to, end) - start;
      if (b <= a) continue;
      const fresh = node.splitText(a);
      if (b - a < fresh.data.length) fresh.splitText(b - a);
      const span = document.createElement('span');
      span.className = 'cv-fresh';
      span.style.animationDelay = `${-Math.round(now - hits[i].t)}ms`;
      fresh.parentNode.insertBefore(span, fresh);
      span.appendChild(fresh);
    }
  });
}

function readTier() {
  try { return localStorage.getItem(TIER_KEY) === 'expert' ? 'expert' : 'fast'; } catch (e) { return 'fast'; }
}

const isCoarsePointer = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

// When an answer lands while the visitor is on another tab, the tab title
// says so until they come back.
const BASE_TITLE = typeof document !== 'undefined' ? document.title : '';
let titleFlagged = false;
function flagTitleUntilSeen() {
  if (typeof document === 'undefined' || titleFlagged) return;
  titleFlagged = true;
  document.title = `● Rob answered — ${BASE_TITLE.replace(/^● Rob answered — /, '')}`;
  const clear = () => {
    if (document.hidden) return;
    document.title = BASE_TITLE;
    titleFlagged = false;
    document.removeEventListener('visibilitychange', clear);
    window.removeEventListener('focus', clear);
  };
  document.addEventListener('visibilitychange', clear);
  window.addEventListener('focus', clear);
}

function isRealMessage(m) {
  return !m.local && !m.safetyCard && !m.nudge && !m.giving;
}

/**
 * First Answer's More (docs/plans/experiments.md) goes under an answer the note asked for short only
 * while it really is short and offered no help (988, a hotline, findhelp.org): an answer that came
 * back whole (the note's own pattern for someone carrying something hard) has nothing more to bring,
 * and More under a reply that offered help would be the wrong next step (the safety review, Oct 21).
 * A short answer is one or two sentences and at most two bullets; a full one runs to hundreds of words.
 */
const MORE_MAX_WORDS = 160;
function moreFits(text) {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean).length;
  return words > 0 && words <= MORE_MAX_WORDS && !offersHelp(text);
}

/**
 * What a draft keeps: the conversation, and its crisis cards. A reload then
 * brings back the card, its numbers, and the conversation's quiet (no
 * support line, no card). Saved chats still drop cards (firebase/db.js), and
 * the model never sees one (utils/compaction.js).
 */
function keptInDraft(m) {
  return isRealMessage(m) || Boolean(m.safetyCard);
}

/**
 * When a window's questions come back, on this device's clock: "4:30 PM",
 * "1:15 AM tomorrow". Rounded up to the minute: a window that ends at
 * 4:30:40 says 4:31, so someone who comes back at the time it gave is
 * never turned away again.
 */
function resetTimeText(resetAt) {
  const at = new Date(Math.ceil(Number(resetAt) / 60000) * 60000);
  if (!resetAt || !Number.isFinite(at.getTime())) return null;
  const time = at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const now = new Date();
  if (at.toDateString() === now.toDateString()) return time;
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  if (at.toDateString() === tomorrow.toDateString()) return `${time} tomorrow`;
  return at.toLocaleString([], { weekday: 'long', hour: 'numeric', minute: '2-digit' });
}

/**
 * This person's window from an answer's metadata (functions/chat.js), or
 * null when they have no limit (the team). An older server sends only
 * remainingToday and dailyLimit, and does not say whether an email or an
 * account would give more (then: only a guest with no email on file).
 */
function quotaFromMetadata(m) {
  const windowed = Number.isFinite(m.windowCount) && m.windowLimit !== undefined;
  if (windowed && m.windowLimit === null) return null;
  const remaining = windowed ? Math.max(0, m.windowLimit - m.windowCount) : m.remainingToday;
  if (remaining == null) return null;
  return {
    remaining,
    limit: windowed ? m.windowLimit : m.dailyLimit,
    resetAt: Number.isFinite(m.resetAt) ? m.resetAt : null,
    emailOnFile: Boolean(m.emailOnFile),
    moreWithEmail: typeof m.moreWithEmail === 'boolean' ? m.moreWithEmail : !m.emailOnFile,
    moreWithAccount: typeof m.moreWithAccount === 'boolean' ? m.moreWithAccount : !m.emailOnFile,
    tierName: m.tierName,
  };
}

// A question that fails on the server's side is given back to its window
// (functions/chat.js), so the questions-left line goes back to what it said.
const GIVEN_BACK = new Set(['upstream', 'xai_blocked', 'no_content', 'internal', 'unavailable']);

/** 'video' | 'blog' | 'book', the way the server and the renderer decide it. */
function sourceKindOf(s) {
  const id = String(s?.videoId || '');
  if (s?.contentType === 'blog' || /^blog:/i.test(id)) return 'blog';
  if (s?.contentType === 'book' || /^book-/i.test(id)) return 'book';
  return 'video';
}

/** The quotes the server found word for word in the book, from an answer's book sources (groundClient). */
function verifiedQuotesOf(sources) {
  return (sources || []).flatMap((s) => (sourceKindOf(s) === 'book' && Array.isArray(s.quotes) ? s.quotes.filter((q) => typeof q === 'string') : []));
}

/** How many of each kind of source an answer drew on, for its label. */
function countKinds(list, kindOf) {
  const seen = new Set();
  const counts = { video: 0, blog: 0, book: 0 };
  (list || []).forEach((s) => {
    const kind = kindOf(s);
    const id = kind === 'book' ? 'book' : String(s.videoId || s.url || s.title || '');
    if (seen.has(`${kind}:${id}`)) return;
    seen.add(`${kind}:${id}`);
    counts[kind] += 1;
  });
  return counts;
}

/**
 * The chat surface shared by Open Search (archive), Focused Study with many
 * sermons (multi) and one sermon (single): messages, streaming with a hard
 * abort on leave, Fast/Expert answer tiers with a live timer, retrieved
 * sources, voice in and out, feedback and the "Ask the real Rob" flow, quota
 * awareness, drafts, and opt-in saving.
 */
function ChatView({
  mode,
  endpoint,
  user,
  chatId,
  resetNonce = 0,
  videos = [],
  videoIds = [],
  chatType = 'collections',
  extraBody = null,
  header = null,
  welcome = null,
  draftKey = null,
  placeholder = 'Ask anything.',
  followupPlaceholder = null,
  singleVideoUrl = null,
  landing = null, // a bank question this tab arrived with (utils/campaign.js)
  prefill = null, // words from a link: they only ever fill the box
  fromAd = false, // a paid ad's link opened this tab (utils/campaign.js isAdArrival)
  onRequestSignIn,
  onDirtyChange,
  onChatSaved,
  onAnswerComplete,
}) {
  const isGuest = Boolean(user?.isAnonymous);
  // Owners and admins (Pastor Rob among them) get "Fix in Zed" under an answer: the team portal opens
  // Zed on that answer, by its request id (src/components/AdminPanel.js ?zedfix=). Visitors never see it.
  const myRole = useContext(TeamRoleContext);
  const canFixInZed = !isGuest && (myRole === 'owner' || myRole === 'admin');
  // Typed-but-unsent text survives view switches and reloads (per tab).
  const composeKey = `apr-compose:${mode}:${(videoIds || []).join(',')}`;

  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [streamStatus, setStreamStatus] = useState(null);
  // What the answer in flight is, from its first frame: More's full answer (First Answer).
  const [streamLabel, setStreamLabel] = useState('');
  const [thinking, setThinking] = useState('');
  const [liveSources, setLiveSources] = useState([]);
  // What the server says was in front of the model for the answer in flight
  // (ground, inside its sources events): a book citation is a number only for
  // a chapter it read. Null for a server or a person that sends none.
  const [liveGround, setLiveGround] = useState(null);
  const [currentChatId, setCurrentChatId] = useState(chatId);
  const [saved, setSaved] = useState(Boolean(chatId));
  const [saving, setSaving] = useState(false);
  const [compaction, setCompaction] = useState(null);
  const [tier, setTier] = useState(readTier);
  const [activeTier, setActiveTier] = useState(null); // tier of the answer in flight
  const [startedAt, setStartedAt] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [expected, setExpected] = useState(null);
  const [quota, setQuota] = useState(null); // { remaining, limit, resetAt, emailOnFile, moreWithEmail, moreWithAccount, tierName }
  // { resetAt, moreWithAccount, quiet, support } when the window's questions
  // are used: quiet is a safety hold (the 988 line), support whether the
  // support line was due when the card appeared (it stays while it is shown).
  const [limitReached, setLimitReached] = useState(null);
  // A guest's question waiting on an email: { question, options, key, resetAt }.
  // `key` is the question already on screen (null for a "Go deeper").
  const [emailGate, setEmailGate] = useState(null);
  const [quotaJoin, setQuotaJoin] = useState(false); // the email form under the questions-left line
  const [joinedNote, setJoinedNote] = useState(false); // "Thanks! You're in." for a moment
  const [liveSupport, setLiveSupport] = useState(false); // the support line under the answer in flight
  const publicConfig = usePublicConfig();
  // Whether the asks may ask, rest or are held on this device (utils/giving.js),
  // and whether a tap on one opens the support sheet (GiveSheet).
  const giving = useGivingState();
  const giveSheetOn = sheetOn(publicConfig);
  // Bible references in finished answers (utils/scripture.js): 0 plain words,
  // 1 a tap opens the passage, 2 the same with a faint dotted line. Only on
  // or off reaches the rendered answers; the line and the site never do.
  const scriptureLevel = scriptureModeNow(publicConfig);
  const scriptureOn = scriptureLevel > 0;
  const [showJump, setShowJump] = useState(false);
  // The welcome screen's starters: the bank's seeker set, four at a time
  // (BRAND_SPEC §11.1), never the insider deck.
  const [welcomeQuestions, setWelcomeQuestions] = useState(() => sampleStarters(4).map((q) => q.text));
  // Front Door (utils/experiments.js, docs/plans/experiments.md): decided once, at the welcome's
  // first paint, for the main chat's welcome only. Bare is the welcome without its questions and
  // without More questions; everything else about it is as always, its first view's times
  // included. Nobody is shown which they got.
  const [frontDoor] = useState(() => (mode === 'archive' && welcome && welcome.starters
    ? frontDoorFor({ guest: Boolean(user?.isAnonymous) }) : null));
  const bareWelcome = Boolean(frontDoor && frontDoor.bare);
  // The welcome set opens on the ads' question; "More questions" sets don't.
  const [startersPinned, setStartersPinned] = useState(true);
  const [glimmerRun, setGlimmerRun] = useState(0); // a new value starts a new glimmer pass
  const glimmerReplays = useRef(0);
  // The default welcome's "Ask anything." types out on the first welcome of
  // a visit (WelcomeTitle): never on an ad's landing (its question types into
  // the box instead), never for a visitor a paid ad sent to the front page
  // without a question (fromAd: they tapped to ask something, so the whole
  // first view, the ad's question first among the starters, is there from
  // the first frame with the caret in the box, as after a reload), never with
  // reduced motion, nor in a high-contrast mode (where it couldn't be seen
  // typing). True until it has typed.
  const [welcomeTyping, setWelcomeTyping] = useState(() => Boolean(welcome && welcome.typeOn)
    && !landing && !prefill && !fromAd && !prefersReducedMotion() && !forcedColors() && !typedThisVisit());
  // While it types, the rest of the first view waits, and then comes in part by
  // part (REVEAL): null once everything is there, else { start, dropped, at }:
  // when the typing started (performance.now(), null until it has), whether
  // the caret has dropped into the box, and the latest of REVEAL_MARKS
  // reached. Only with the typing (so never on an ad's landing, a visitor a
  // paid ad sent (fromAd), a link with words, a repeat view, with reduced
  // motion or in a high-contrast mode), and
  // never on a device under a crisis hold (utils/giving.js: a crisis card in
  // the last 24 hours), where the AI notice's crisis link and the menu's 988
  // line are there from the first frame. Only once: anyone starting, or the
  // welcome going away or out of sight, ends it at once, and it never comes back.
  const [reveal, setReveal] = useState(() => (welcomeTyping && getGivingState().ask !== 'hold'
    ? { start: null, dropped: false, at: -1 } : null));
  // The AI notice has been on screen, in a frame the browser painted, since
  // the reveal ended (or there never was one, or it never holds the notice
  // back): until then nothing is asked.
  const noticeShownRef = useRef(reveal == null || !NOTICE_STAGED);
  const heldSendRef = useRef(false); // a question waiting for that frame
  // True while the welcome's caret blinks in the box (four blinks, then
  // still), and from the first render until then: the starters' glimmer
  // waits for it, so one thing moves at a time (§9). A glimmer pass that
  // starts and is cut off is spent, so this is held from the start, not
  // switched on a frame later. The caret's animation events keep it true.
  const [caretBlinking, setCaretBlinking] = useState(() => Boolean(welcome && welcome.typeOn)
    && !landing && !prefill && !prefersReducedMotion());
  const [feedbackGiven, setFeedbackGiven] = useState({});
  const [copiedKey, setCopiedKey] = useState(null);
  const [askRobFor, setAskRobFor] = useState(null); // message timestamp with the panel open
  const [askRobFocus, setAskRobFocus] = useState(false); // opened from a human step: take focus
  // What the server said of a note sent to Rob, by answer: { safety, minor } (functions/feedback.js).
  const [robReplies, setRobReplies] = useState({});
  const [chromeCollapsed, setChromeCollapsed] = useState(false); // compact composer (phones)

  // Arrival from an ad (utils/campaign.js): 'idle' | 'typing' (the question
  // types into the box) | 'asked'. A statement or your-turn landing is a
  // heading with no question asked for them.
  const [arrivalPhase, setArrivalPhase] = useState('idle');
  const [landingHeader, setLandingHeader] = useState(null);
  const [inputFocused, setInputFocused] = useState(false);
  const [ladderOnScreen, setLadderOnScreen] = useState(false);
  const [announce, setAnnounce] = useState(''); // polite, throttled: started / ready
  // "Ask about this" (utils/quote.js): what words selected in a finished
  // answer offer ({ text, from }, or null), and the quote waiting above the box.
  const [askOffer, setAskOffer] = useState(null);
  const [quoteDraft, setQuoteDraft] = useState(null);
  const [quoteHintFits, setQuoteHintFits] = useState(true); // whether QUOTE_HINT fits the box (hintFits)

  // Voice
  const [speakingKey, setSpeakingKey] = useState(null);
  const [speechState, setSpeechState] = useState('idle');
  const [recording, setRecording] = useState(false);
  const [micStream, setMicStream] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  // "AI Rob, in Rob's voice": one setting for the app, switched in the menu
  // (utils/voicePreference.js). Off on campaign landings (App), whatever was
  // saved; a person can still turn it on.
  const voiceReplies = useVoiceReplies();
  // A team member's pick of voice (utils/teamVoice.js; null for everyone else), and who the
  // server says read the latest chunk ({ voice, fallback }: only a team member's requests say).
  const teamVoice = useTeamVoice();
  const [voiceHeard, setVoiceHeard] = useState(null);
  // Who is signed in, for the player's cache: another person's recordings are never replayed.
  const userRef = useRef(user);
  userRef.current = user;

  const messagesEndRef = useRef(null);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const inputRowRef = useRef(null);
  // What the box holds now, for the voice flow, which outlives the render that started it.
  const inputMessageRef = useRef('');
  inputMessageRef.current = inputMessage;
  // "Ask" pressed while the mic listens (or while it transcribes): what was said is asked.
  const askAfterVoiceRef = useRef(false);
  const sendMessageRef = useRef(null); // this render's sendMessage, for that
  const playerRef = useRef(null);
  const recorderRef = useRef(null);
  const abortRef = useRef(null);
  const requestIdRef = useRef(null);
  const pendingCancelRef = useRef(null);
  const genRef = useRef(0);
  // First Answer (utils/experiments.js): the watch for leaving within 15 seconds of a first answer's
  // end, { cid, watch }, until they go on, start another conversation, or the time is up.
  const faWatchRef = useRef(null);
  const voiceRepliesRef = useRef(voiceReplies);
  voiceRepliesRef.current = voiceReplies;
  const nearBottomRef = useRef(true);
  // Compact composer bookkeeping (see the "compact composer" section)
  const chromeCollapsedRef = useRef(false);
  const narrowRef = useRef(false);
  const idleTimerRef = useRef(null);
  const lastScrollTopRef = useRef(0);
  const scrollDownRunRef = useRef(0);
  const scrollUpRunRef = useRef(0);
  const userScrollUntilRef = useRef(0);
  const settleUntilRef = useRef(0); // scroll events before this are the chrome sliding
  // Reading chrome: the shell's top bar + one-line disclosure (AppShell) and
  // this view's own header tuck away together while a conversation is read.
  const { tucked: shellTucked, setTucked: setShellTucked } = useContext(ShellChrome);
  const tuckedRef = useRef(shellTucked);
  tuckedRef.current = shellTucked;
  const rootRef = useRef(null);
  const headerSlotRef = useRef(null);
  // Reading position (see the "reading position" section)
  const streamElRef = useRef(null); // the in-flight answer bubble
  const streamBodyRef = useRef(null); // its markdown body, where words ink in
  const tailSpaceRef = useRef(null); // lets a short answer's question reach the top
  const anchorRef = useRef(null); // { key, question } | { stream: true }: what the view is parked on
  const pendingAnchorRef = useRef(false); // scroll to the anchor after the next render
  const scrollOnLoadRef = useRef(false); // a loaded conversation opens at its end
  const revealEndRef = useRef(false); // bring an out-of-band notice into view
  const revealMoreRef = useRef(null); // a short first answer's More, brought into view as it ends
  const scrolledSinceAskRef = useRef(false); // a finger or wheel moved the view since the question
  const fadeRef = useRef({ html: '', settled: 0, spans: [] });
  const settledKeysRef = useRef(new Set()); // answers that replaced their streaming bubble
  const draftHandledRef = useRef(false);
  const wasAnonymousRef = useRef(user?.isAnonymous);
  // Mirror of the answer in flight, for the page-hide safety net below.
  const streamingRef = useRef({ active: false, text: '', tier: null, sources: [] });
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const quotaRef = useRef(quota);
  quotaRef.current = quota;
  const emailGateRef = useRef(emailGate);
  emailGateRef.current = emailGate;
  const gateTitleId = useId();
  // An arrival is handled once per mount; its typing timer survives renders.
  const landingHandledRef = useRef(false);
  const arrivalTimerRef = useRef(null);
  const ladderRef = useRef(null);
  const renderCacheRef = useRef(new WeakMap()); // message -> rendered answer
  const flashTimerRef = useRef(null);
  const scriptureDownRef = useRef(null); // the press on a Bible reference, until its click (openScriptureFrom)
  const untabbedRef = useRef(new Set()); // references whose tab stop a press set aside (noteScripturePress)
  const refocusRef = useRef(null); // the one of those the keyboard had focused, to give the focus back to
  // The Genius reader: what is open, and what to give focus back to.
  const [reader, setReader] = useState(null);
  const readerOpenerRef = useRef(null);
  // "Ask about this": the offer and the quote as they are now (the voice flow
  // and the timers outlive the render that started them), whether words in an
  // answer are selected right now (the chrome holds still meanwhile), what
  // moved last ('mouse' | 'touch' | 'key'), whether the box had focus as a
  // press began, and the selection effect's own read, for when an answer ends.
  const askOfferRef = useRef(null);
  const quoteShownRef = useRef(null); // set with quoteShown, below
  const selectionLiveRef = useRef(false);
  const lastInputRef = useRef('mouse');
  const boxFocusedAtPressRef = useRef(false);
  const readSelectionRef = useRef(null);
  const loadingRef = useRef(false);
  loadingRef.current = loading;
  // The email gate or the window's limit card in the box's place: there is no
  // box to ask with, so nothing is offered (or announced) until it is back.
  const boxGone = Boolean(emailGate || limitReached);
  const boxGoneRef = useRef(false);
  boxGoneRef.current = boxGone;
  const quoteChipId = useId();
  const askHintId = useId();

  // The answer in flight, rendered once per chunk (not on every timer tick).
  // No Sources footer until it is finished: its text would sit after the
  // newest words in textContent, where the ink-in offsets would land on it.
  // AI Rob's grey caret at the insertion point is CSS (ChatView.css), so it
  // adds nothing to textContent either.
  const streamHtml = useMemo(
    () => (streamingText
      ? renderMarkdown(streamingText, { videos, sources: liveSources, singleVideoUrl, showTitle: mode !== 'single', footer: false, ground: liveGround })
      : ''),
    [streamingText, videos, liveSources, singleVideoUrl, mode, liveGround],
  );

  // ------------------------------------------------------------------ voice

  const pushLocalNotice = useCallback((text) => {
    // Out-of-band notices (mic, voice, save, stop) land at the end of the
    // conversation: bring them into view if the visitor is already there.
    // Never while an answer is streaming — the view is theirs then.
    if (!abortRef.current && nearBottomRef.current) revealEndRef.current = true;
    setMessages((prev) => [...prev, { role: 'assistant', local: true, content: text, timestamp: new Date().toISOString() }]);
  }, []);

  const getPlayer = useCallback(() => {
    if (!playerRef.current) {
      playerRef.current = new SpeechPlayer({
        onStateChange: (state) => {
          setSpeechState(state);
          if (state === 'idle') setSpeakingKey(null);
        },
        onError: (err) => {
          // A refusal the server explains (the day's voice used up, the network's or the site's,
          // a voice-guard refusal) is said in its words; anything else is a failure to report.
          const explained = err?.safeMessage && [403, 429].includes(Number(err.status));
          if (!explained) reportError(err, { where: 'voice.playback' });
          pushLocalNotice(explained ? err.safeMessage : "Couldn't play that reply aloud right now. You can keep chatting by text.");
        },
        onVoice: setVoiceHeard,
        // How each reading went, for the counts (utils/analytics.js): never which answer.
        onReading: trackVoice,
        // At each request: who is signed in, the browser's team mark, the team's pick of voice.
        context: () => ({ uid: userRef.current?.uid || null, int: isInternalBrowser(), voice: teamVoicePick() }),
      });
    }
    return playerRef.current;
  }, [pushLocalNotice]);

  useEffect(() => () => { if (playerRef.current) playerRef.current.dispose(); }, []);

  // The page closing ends a reading by 'leave', before the counts' last batch goes (utils/analytics.js).
  useEffect(() => onLeaving(() => { if (playerRef.current) playerRef.current.stop('leave'); }), []);

  // A Listen press reads one answer: a second press on the one reading stops it. Its request id
  // and grant go with it, so the server can tell it is AI Rob's own answer (utils/voice.js).
  const speakMessage = (key, content, { crisis = false, src = 'listen', rid = null, grant = null } = {}) => {
    if (speakingKey === key && speechState !== 'idle') {
      getPlayer().stop('stop');
      return;
    }
    setSpeakingKey(key);
    getPlayer().play(content, { crisis, src, rid, grant });
  };

  // The setting changes in the menu; this surface follows it inside the same
  // tap (listeners run synchronously), so iOS lets the player prime there.
  useEffect(() => subscribeVoiceReplies((next) => {
    voiceRepliesRef.current = next;
    if (next) {
      if (isShown()) getPlayer().unlock();
    } else if (playerRef.current) {
      playerRef.current.stop('off');
    }
  }), [getPlayer]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMicClick = async () => {
    if (transcribing) return;
    if (recording) {
      recorderRef.current?.stop();
      return;
    }
    // Speaking to it starts the conversation: the AI notice is on screen first.
    if (!noticeShownRef.current) {
      flushSync(() => setReveal(null));
      noticeIn({ force: true });
    }
    // Asking by voice is starting the next question, as typing is.
    if (playerRef.current) playerRef.current.stop('type');
    try {
      getPlayer().unlock();
      const rec = startRecording({ maxMs: 60000, onStream: setMicStream });
      recorderRef.current = rec;
      setRecording(true);
      const captured = await rec.result;
      setRecording(false);
      setMicStream(null);
      if (!captured.blob.size) return;
      setTranscribing(true);
      const text = await transcribe(captured);
      if (text) {
        const words = inputMessageRef.current ? `${inputMessageRef.current} ${text}` : text;
        if (askAfterVoiceRef.current) {
          // "Ask" was pressed while they spoke: what they said is asked, as
          // if they had typed it and pressed Ask (the latest render's send),
          // with the quote above the box if there is one.
          askAfterVoiceRef.current = false;
          sendMessageRef.current(words, quoteShownRef.current ? { quote: quoteShownRef.current } : {});
          return;
        }
        setInputMessage(words);
        setTimeout(adjustTextareaHeight, 0);
        textareaRef.current?.focus();
      } else {
        pushLocalNotice("I didn't catch that — try speaking again, a little closer to the mic.");
      }
    } catch (err) {
      setRecording(false);
      setMicStream(null);
      if (err?.message === 'cancelled') return;
      if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
        pushLocalNotice('Microphone access is blocked. Allow the mic in your browser settings to ask by voice.');
      } else {
        reportError(err, { where: 'voice.input' });
        pushLocalNotice(mapChatError(err));
      }
    } finally {
      askAfterVoiceRef.current = false;
      setTranscribing(false);
      recorderRef.current = null;
    }
  };

  // ----------------------------------------------------------- lifecycle

  /** Hard stop for whatever is in flight: fetch, speech, mic. */
  const cancelInFlight = useCallback(() => {
    genRef.current += 1;
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
      // Tell the server too: frees our one-at-a-time lock and stops the
      // model call even when the dropped connection isn't noticed upstream.
      const requestId = requestIdRef.current;
      if (requestId) {
        pendingCancelRef.current = authedFetch('cancelChat', { requestId })
          .catch(() => {})
          .finally(() => { pendingCancelRef.current = null; });
      }
    }
    requestIdRef.current = null;
    if (playerRef.current) playerRef.current.stop('stop');
    if (recorderRef.current) {
      recorderRef.current.cancel();
      recorderRef.current = null;
      setRecording(false);
      setMicStream(null);
    }
    // The abandoned answer is gone: the page-hide safety net must not save it
    // later as an "interrupted" reply (into this chat or the one replacing
    // it), and the view is no longer parked on its question.
    streamingRef.current = { active: false, text: '', tier: null, sources: [], ground: null };
    anchorRef.current = null;
    pendingAnchorRef.current = false;
    revealEndRef.current = false;
    setLoading(false);
    setStreamingText('');
    setStreamStatus(null);
    setThinking('');
    setLiveSources([]);
    setLiveGround(null);
    setLiveSupport(false);
    setStartedAt(null);
    setActiveTier(null);
  }, []);

  useEffect(() => () => cancelInFlight(), [cancelInFlight]);
  useEffect(() => () => { if (faWatchRef.current) faWatchRef.current.watch.cancel(); }, []);

  /**
   * First Answer (utils/experiments.js): someone went on from the first answer of a conversation
   * this tab enrolled in the test, by More, a follow-up, Go deeper or Ask the real Rob. Each is
   * counted once (More and a follow-up count as going on too), and the watch for leaving is over.
   * Nothing for a conversation this tab didn't enrol (another tab, the draft brought back later),
   * so nothing is counted twice.
   */
  const wentOn = (conversationId, metric) => {
    if (!conversationId) return;
    noteFirstAnswer(conversationId, metric);
    if (faWatchRef.current && faWatchRef.current.cid === conversationId) {
      faWatchRef.current.watch.cancel();
      faWatchRef.current = null;
    }
  };

  /**
   * A question the email gate held, back in the box: its words, and the quote
   * it asked about above them. An empty box that asked "Tell me more about
   * this." gets its empty box back, with the quote.
   */
  const restoreHeldQuestion = (gate) => {
    const quote = cleanQuote(gate.options && gate.options.quote);
    setInputMessage(quote && gate.question === defaultAsk(quote) ? '' : gate.question);
    if (quote) setQuoteDraft(quote);
  };

  /**
   * The email gate closes without an email (sign in instead, the window
   * ended, another account): the waiting question goes back into the box,
   * ready to send, instead of sitting unanswered above it.
   */
  const releaseGate = useCallback(() => {
    const gate = emailGateRef.current;
    if (!gate) return;
    emailGateRef.current = null;
    setEmailGate(null);
    if (!gate.key) return;
    setMessages((prev) => {
      const i = prev.findIndex((m) => m.role === 'user' && m.timestamp === gate.key);
      if (i === -1 || prev.slice(i + 1).some((m) => m.role === 'assistant' && isRealMessage(m))) return prev;
      return prev.filter((_, j) => j !== i);
    });
    restoreHeldQuestion(gate);
    setTimeout(adjustTextareaHeight, 0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When the window ends its questions are back: the card goes, a question
  // that was waiting returns to the box, and the questions-left line (which
  // would still say "No questions left until 4:30 PM") goes with them.
  useEffect(() => {
    const resetAt = (emailGate && emailGate.resetAt) || (limitReached && limitReached.resetAt) || (quota && quota.resetAt);
    if (!resetAt) return undefined;
    const t = setTimeout(() => {
      releaseGate();
      setLimitReached(null);
      setQuota(null);
    }, Math.min(Math.max(0, resetAt - Date.now()) + 1000, 2147483647));
    return () => clearTimeout(t);
  }, [emailGate, limitReached, quota, releaseGate]);

  // "Thanks! You're in." stays a moment, then the box is just the box again.
  useEffect(() => {
    if (!joinedNote) return undefined;
    const t = setTimeout(() => setJoinedNote(false), 6000);
    return () => clearTimeout(t);
  }, [joinedNote]);

  // Composer draft: restore once on mount, then keep sessionStorage in sync.
  // A quote waiting above the box comes back with the words (`:quote`).
  const quoteKey = `${composeKey}:quote`;
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(composeKey);
      if (saved) {
        setInputMessage(saved);
        setTimeout(adjustTextareaHeight, 0);
      }
      const savedQuote = cleanQuote(JSON.parse(sessionStorage.getItem(quoteKey) || 'null'));
      if (savedQuote) setQuoteDraft(savedQuote);
    } catch (e) { /* storage blocked, or not a quote */ }
  }, [composeKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    try {
      if (quoteDraft) sessionStorage.setItem(quoteKey, JSON.stringify(quoteDraft));
      else sessionStorage.removeItem(quoteKey);
    } catch (e) { /* storage blocked */ }
  }, [quoteDraft, quoteKey]);
  useEffect(() => {
    if (arrivalPhase === 'typing') return undefined; // the ad's words, not theirs
    const timer = setTimeout(() => {
      try {
        if (inputMessage) sessionStorage.setItem(composeKey, inputMessage);
        else sessionStorage.removeItem(composeKey);
      } catch (e) { /* storage blocked */ }
    }, 250);
    return () => clearTimeout(timer);
  }, [inputMessage, composeKey, arrivalPhase]);

  // Words from a link (a ?q= that is not a bank ID, or #ask= from
  // thenet.org) only ever fill the box. Nothing is sent for anyone
  // (BRAND_SPEC §15.1), and on a phone the keyboard stays down.
  useEffect(() => {
    if (!prefill || landing) return;
    setInputMessage(prefill);
    setTimeout(adjustTextareaHeight, 0);
  }, [prefill]); // eslint-disable-line react-hooks/exhaustive-deps

  // If the page is closed or reloaded mid-answer, keep what had arrived so
  // the visitor does not come back to nothing.
  useEffect(() => {
    if (!draftKey) return undefined;
    const persistPartial = () => {
      const s = streamingRef.current;
      if (!s.active || !s.text) return;
      const kept = messagesRef.current.filter(keptInDraft);
      writeDraft(draftKey, [...kept, {
        role: 'assistant', content: s.text, timestamp: new Date().toISOString(),
        tier: s.tier, sources: s.sources || [], interrupted: true, ...(s.ground ? { ground: s.ground } : {}),
      }], currentChatId);
    };
    const onVisibility = () => { if (document.visibilityState === 'hidden') persistPartial(); };
    window.addEventListener('pagehide', persistPartial);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', persistPartial);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [draftKey, currentChatId]);

  // Signing in or creating an account (a guest whose window ran out, now on
  // an account) starts the allowance over: drop the old count, the limit
  // card and the email gate (its question goes back into the box).
  useEffect(() => {
    setQuota(null);
    setLimitReached(null);
    setQuotaJoin(false);
    releaseGate();
  }, [user?.uid, user?.isAnonymous]); // eslint-disable-line react-hooks/exhaustive-deps

  // Chat switch / new chat: abandon the in-flight answer so it can never
  // stream into the conversation that replaced it, then load what belongs here.
  // The box empties for a different conversation. On the first mount it keeps
  // what the effects above just put in it (words typed and not sent before a
  // reload in this tab, or a link's words): emptying it here wiped both, and
  // the saved copy of the typed words with them. An ad's arrival still starts
  // with an empty box (its question types into it).
  const boxForRef = useRef(null);
  useEffect(() => {
    cancelInFlight();
    // Another conversation: First Answer's watch for leaving is over (they stayed).
    if (faWatchRef.current) {
      faWatchRef.current.watch.cancel();
      faWatchRef.current = null;
    }
    setCurrentChatId(chatId ?? null);
    setSaved(Boolean(chatId));
    setCompaction(null);
    setLimitReached(null);
    setQuotaJoin(false);
    setAskRobFor(null);
    setRobReplies({});
    const boxFor = `${chatId ?? ''}|${resetNonce}|${mode}`;
    if (boxForRef.current !== boxFor && (boxForRef.current != null || landing)) {
      // And a quote from the conversation that was here goes with its words.
      setInputMessage('');
      setQuoteDraft(null);
    }
    boxForRef.current = boxFor;
    askOfferRef.current = null;
    setAskOffer(null);
    // A question the email gate was holding is never lost: it moves into
    // the new conversation's box, with its quote.
    const gate = emailGateRef.current;
    if (gate) {
      emailGateRef.current = null;
      setEmailGate(null);
      if (gate.key) restoreHeldQuestion(gate);
    }
    // A different conversation: nothing is parked, the top bar comes back,
    // and a conversation that loads opens at its latest message.
    anchorRef.current = null;
    pendingAnchorRef.current = false;
    revealEndRef.current = false;
    scrollOnLoadRef.current = false;
    if (tailSpaceRef.current) tailSpaceRef.current.style.height = '0px';
    setTuck(false);
    expandChrome();
    armChromeIdle();

    if (resetNonce > 0) {
      // A new conversation: no landing heading, fresh starters.
      setLandingHeader(null);
      setArrivalPhase('idle');
      if (arrivalTimerRef.current) { clearInterval(arrivalTimerRef.current); arrivalTimerRef.current = null; }
    }

    if (chatId) {
      scrollOnLoadRef.current = true;
      getChat(user.uid, chatId)
        .then((chat) => { if (chat?.messages) setMessages(chat.messages); })
        .catch((err) => reportError(err, { where: 'chat.load' }));
    } else if (landing && !landingHandledRef.current && resetNonce === 0) {
      // Arriving from an ad starts a fresh conversation for its question
      // (the arrival effect below asks it); an old unsaved draft yields.
      draftHandledRef.current = true;
      clearDraft(draftKey);
      setMessages([]);
    } else if (draftKey && !draftHandledRef.current && resetNonce === 0) {
      // First mount of a fresh session: restore an unsaved conversation that a
      // reload or crash would otherwise have destroyed.
      draftHandledRef.current = true;
      const draft = readDraft(draftKey);
      if (draft) {
        scrollOnLoadRef.current = true;
        // Back from Pushpay in the same tab (Facebook's and Instagram's own
        // browsers, or iOS dropping the tab) within half an hour: one local
        // note ends the conversation, once. It thanks them for the thought,
        // never for a gift nobody can see, and is never saved or sent to the
        // model. Not in a conversation that had a crisis card, nor under a hold.
        let note = null;
        if (returnNoteDue(Date.now(), readGiveOut())) {
          clearGiveOut();
          if (!draft.messages.some((m) => m.safetyCard) && getGivingState().ask !== 'hold') {
            note = { role: 'assistant', local: true, content: GIVE_COPY.returnNote, timestamp: new Date().toISOString() };
            setAnnounce(GIVE_COPY.returnNote);
          }
        }
        setMessages(note ? [...draft.messages, note] : draft.messages);
        if (draft.chatId) setCurrentChatId(draft.chatId);
      } else {
        setMessages([]);
      }
    } else {
      draftHandledRef.current = true;
      clearDraft(draftKey);
      setMessages([]);
      setWelcomeQuestions(sampleStarters(4).map((q) => q.text));
      setStartersPinned(true);
      glimmerReplays.current = 0;
      setGlimmerRun((r) => r + 1);
      // The welcome's caret comes back (keyed by resetNonce, so it blinks
      // again even if the welcome never left): the glimmer waits for it.
      setCaretBlinking(Boolean(welcome && welcome.typeOn) && !prefersReducedMotion());
    }
  }, [chatId, resetNonce, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Draft sync (unsaved conversation only)
  useEffect(() => {
    if (!draftKey) return;
    if (saved) clearDraft(draftKey);
    else if (messages.some(isRealMessage)) writeDraft(draftKey, messages.filter(keptInDraft), currentChatId);
  }, [messages, saved, currentChatId, draftKey]);

  // Unsaved-conversation reporting for the app-level "let it go?" guard
  useEffect(() => {
    if (!onDirtyChange) return undefined;
    onDirtyChange(messages.filter(isRealMessage).length > 0 && !saved);
    return undefined;
  }, [messages, saved]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (onDirtyChange) onDirtyChange(false); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Guest → account mid-conversation: quietly keep what is on screen.
  useEffect(() => {
    const wasAnonymous = wasAnonymousRef.current;
    wasAnonymousRef.current = user?.isAnonymous;
    if (!wasAnonymous || user?.isAnonymous) return;
    const real = messages.filter(isRealMessage);
    if (real.length === 0 || saved) return;
    const idToUse = currentChatId || newChatId();
    setCurrentChatId(idToUse);
    saveChatWithMessages(user.uid, idToUse, videoIds, messages, chatType)
      .then(() => { setSaved(true); if (onChatSaved) onChatSaved(idToUse); })
      .catch((err) => reportError(err, { where: 'chat.autosaveAfterSignup' }));
  }, [user?.isAnonymous]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ticking timer while an answer is in flight
  useEffect(() => {
    if (!startedAt) { setElapsedMs(0); return undefined; }
    const id = setInterval(() => setElapsedMs(Date.now() - startedAt), 100);
    return () => clearInterval(id);
  }, [startedAt]);

  // ------------------------------------------------- reading position
  // The view never chases a streaming answer. Sending parks the new question
  // at the top of the screen and the answer inks in beneath it, from its first
  // word; the visitor scrolls wherever they like and "↓" catches up on demand.
  // A spacer after the conversation gives a short answer the room it needs
  // for its question to reach the top, and shrinks exactly as the answer
  // grows, so the page never jumps. Everything here works on refs so the
  // ResizeObserver and the timers can call it from any render.

  /** An element's y within the scroller's content. */
  const toContentY = (el) => {
    const scroller = scrollRef.current;
    return el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
  };

  /** How far the conversation's last line sits below the visible area (negative: above it). */
  const contentGapBelow = () => {
    const scroller = scrollRef.current;
    const end = messagesEndRef.current;
    if (!scroller || !end) return 0;
    return end.getBoundingClientRect().bottom - scroller.getBoundingClientRect().bottom;
  };

  /** The scrollTop that parks the current anchor, or null when nothing is parked. */
  const anchorTarget = () => {
    const scroller = scrollRef.current;
    const anchor = anchorRef.current;
    if (!scroller || !anchor) return null;
    const el = anchor.stream
      ? streamElRef.current
      : scroller.querySelector(`[data-key="${anchor.key}"]`);
    if (!el) return null;
    let target = toContentY(el) - ANCHOR_GAP;
    // A very long question must not push the start of its answer off screen.
    const next = anchor.question ? el.nextElementSibling : null;
    if (next && next.classList.contains('cv-msg')) {
      target = Math.max(target, toContentY(next) - Math.round(scroller.clientHeight * 0.45));
    }
    return Math.max(0, Math.round(target));
  };

  const updateTailSpace = () => {
    const scroller = scrollRef.current;
    const spacer = tailSpaceRef.current;
    if (!scroller || !spacer) return;
    const current = spacer.offsetHeight;
    const target = anchorTarget();
    let need = 0;
    if (target != null) {
      const padBottom = parseFloat(getComputedStyle(scroller).paddingBottom) || 0;
      const contentHeight = toContentY(spacer) + padBottom; // everything above the spacer
      need = Math.max(0, Math.ceil(target + scroller.clientHeight - contentHeight));
    }
    if (Math.abs(need - current) >= 1) spacer.style.height = `${need}px`;
  };

  const scrollToBottom = (behavior = 'smooth') => {
    const scroller = scrollRef.current;
    const end = messagesEndRef.current;
    if (!scroller || !end) return;
    const top = Math.max(0, Math.round(toContentY(end) + end.offsetHeight + 24 - scroller.clientHeight));
    userScrollUntilRef.current = 0; // an app-made scroll never tucks or folds anything
    scroller.scrollTo({ top, behavior: behavior === 'smooth' && prefersReducedMotion() ? 'auto' : behavior });
  };

  // After every change to what is on screen: resize the spacer, then honour
  // whichever single scroll the change asked for.
  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    updateTailSpace();
    if (pendingAnchorRef.current) {
      const target = anchorTarget();
      if (target != null) {
        pendingAnchorRef.current = false;
        userScrollUntilRef.current = 0;
        scroller.scrollTo({ top: target, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      }
    } else if (scrollOnLoadRef.current && messages.length) {
      scrollOnLoadRef.current = false;
      scrollToBottom('auto');
    } else if (revealEndRef.current) {
      revealEndRef.current = false;
      scrollToBottom('smooth');
    } else if (revealMoreRef.current && !loading) {
      // A short first answer ends with its More in view (a small phone, an app's
      // browser), unless the visitor has moved the view themselves since asking.
      const key = revealMoreRef.current;
      revealMoreRef.current = null;
      const reveal = () => {
        const row = scroller.querySelector(`[data-key="${key}"] .cv-more-answer-row`);
        const words = scroller.querySelector(`[data-key="${key}"] .cv-answer`);
        if (!row || scrolledSinceAskRef.current) return;
        let top = Math.round(toContentY(row) + row.offsetHeight + 16 - scroller.clientHeight);
        // Never past the answer's first line: the heart of it stays on screen.
        if (words) top = Math.min(top, Math.round(toContentY(words) - 8));
        if (top > scroller.scrollTop + 1) {
          userScrollUntilRef.current = 0;
          scroller.scrollTo({ top, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        }
      };
      const rowNow = scroller.querySelector(`[data-key="${key}"] .cv-more-answer-row`);
      const fits = rowNow && toContentY(rowNow) + rowNow.offsetHeight + 16 - scroller.clientHeight <= scroller.scrollTop;
      if (!fits && narrowRef.current && !chromeCollapsedRef.current) {
        // A small phone: fold the composer's toolbar first (as a finger scroll does), then look again.
        collapseChrome();
        setTimeout(reveal, 360);
      } else reveal();
    }
    // New words may have streamed in below the fold: offer "↓".
    const below = contentGapBelow();
    nearBottomRef.current = below < 120;
    setShowJump(below > 240);
  }, [messages, loading, streamHtml, askRobFor]); // eslint-disable-line react-hooks/exhaustive-deps

  // The visible area changes size when the chrome tucks or the composer folds.
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => updateTailSpace());
    ro.observe(scroller);
    return () => ro.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // New words ink in (see inkFreshText). Runs only when React has actually
  // replaced the answer's HTML; otherwise the spans from last time still stand.
  useLayoutEffect(() => {
    const body = streamBodyRef.current;
    const fade = fadeRef.current;
    if (!streamHtml || !body) {
      fade.html = '';
      fade.settled = 0;
      fade.spans = [];
      return;
    }
    if (fade.html === streamHtml) return;
    fade.html = streamHtml;
    if (prefersReducedMotion()) return;
    try {
      const now = performance.now();
      const total = body.textContent.length;
      if (total > fade.settled) fade.spans.push({ from: fade.settled, to: total, t: now });
      fade.settled = total;
      fade.spans = fade.spans
        .filter((s) => now - s.t < FADE_MS && s.from < total)
        .map((s) => (s.to > total ? { ...s, to: total } : s));
      if (fade.spans.length) inkFreshText(body, fade.spans, now);
    } catch (e) { /* purely cosmetic: the text is already on screen */ }
  }, [streamHtml]);

  // -------------------------------------- compact chrome (phones)
  // On a phone the tier/voice/save row and the disclaimer fold away after
  // CHROME_IDLE_MS without a touch, or as soon as a finger scrolls down into
  // the conversation, and the input row drops to the bottom edge; focusing or
  // typing in the box unfolds them. The same scroll down also tucks the top
  // bar away (the AI disclosure condenses to one line) and this view's own
  // header; a scroll back up, or reaching the top, brings them back. Wider
  // screens never fold or tuck.

  // While the chrome slides, the scroller changes size under the finger and
  // the browser clamps scrollTop to fit; those scroll events are layout, not
  // the visitor, and must not count as a scroll back up (tuck, then untuck).
  const settleChrome = () => { settleUntilRef.current = Date.now() + CHROME_SETTLE_MS; };

  const setTuck = useCallback((next) => {
    if (next && !narrowRef.current) return;
    if (tuckedRef.current === next) return;
    tuckedRef.current = next;
    settleChrome();
    setShellTucked(next);
  }, [setShellTucked]); // eslint-disable-line react-hooks/exhaustive-deps

  const collapseChrome = useCallback(() => {
    if (!narrowRef.current || chromeCollapsedRef.current) return;
    chromeCollapsedRef.current = true;
    settleChrome();
    setChromeCollapsed(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** False while another view covers this one (display:none). */
  const isShown = () => Boolean(rootRef.current && rootRef.current.getClientRects().length);

  const expandChrome = useCallback(() => {
    clearTimeout(idleTimerRef.current);
    if (!chromeCollapsedRef.current) return;
    chromeCollapsedRef.current = false;
    setChromeCollapsed(false);
    // The toolbar grows back over the end of the conversation. If the last
    // line was on screen, put it back on screen once the fold has opened.
    if (narrowRef.current && contentGapBelow() <= 8) {
      setTimeout(() => {
        const hidden = contentGapBelow();
        if (hidden > 0 && scrollRef.current) {
          userScrollUntilRef.current = 0;
          scrollRef.current.scrollTop += hidden + 8;
        }
      }, 320);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const armChromeIdle = useCallback(() => {
    clearTimeout(idleTimerRef.current);
    if (!narrowRef.current) return;
    idleTimerRef.current = setTimeout(() => {
      // Still composing (keyboard up), or holding words selected in an answer
      // ("Ask about this" waits at the top of the composer): look again later
      // rather than pulling the toolbar out from under the visitor.
      if (document.activeElement === textareaRef.current || selectionLiveRef.current) { armChromeIdle(); return; }
      // Behind another view: nothing to tidy. Showing it again re-arms this.
      if (!isShown()) return;
      collapseChrome();
    }, CHROME_IDLE_MS);
  }, [collapseChrome]); // eslint-disable-line react-hooks/exhaustive-deps

  // A view coming back from behind another one starts fresh — toolbar open,
  // idle clock running — the way AppShell untucks on every view change.
  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') return undefined;
    let shown = null;
    const io = new IntersectionObserver((entries) => {
      const now = entries[entries.length - 1].isIntersecting;
      if (now && shown === false) {
        expandChrome();
        armChromeIdle();
      }
      shown = now;
    });
    io.observe(root);
    return () => io.disconnect();
  }, [expandChrome, armChromeIdle]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(COMPACT_QUERY);
    const apply = () => {
      narrowRef.current = mq.matches;
      if (mq.matches) {
        armChromeIdle();
      } else {
        expandChrome();
        setTuck(false);
      }
    };
    apply();
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else mq.addListener(apply);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', apply);
      else mq.removeListener(apply);
      clearTimeout(idleTimerRef.current);
    };
  }, [armChromeIdle, expandChrome, setTuck]);

  // This view's own header (Focused Study) slides up by its measured height.
  useLayoutEffect(() => {
    const slot = headerSlotRef.current;
    const root = rootRef.current;
    if (!slot || !root) return undefined;
    const apply = () => root.style.setProperty('--cv-header-h', `${slot.offsetHeight}px`);
    apply();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(apply);
    ro.observe(slot);
    return () => ro.disconnect();
  }, [Boolean(header)]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only a finger or a wheel counts as scrolling: the smooth scroll that parks
  // a new question must not tuck or fold anything by itself.
  const noteUserScroll = () => { userScrollUntilRef.current = Date.now() + 1200; scrolledSinceAskRef.current = true; };

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const below = contentGapBelow();
    nearBottomRef.current = below < 120;
    setShowJump(below > 240);

    const top = el.scrollTop;
    const maxTop = el.scrollHeight - el.clientHeight;
    const delta = top - lastScrollTopRef.current;
    lastScrollTopRef.current = top;
    if (top <= 4) setTuck(false); // back at the top: show everything
    const notTheVisitor = Date.now() > userScrollUntilRef.current
      || Date.now() < settleUntilRef.current // the chrome is still sliding
      || top < 0 || top > maxTop + 1; // iOS rubber band: its bounce back is not a scroll
    if (notTheVisitor) {
      scrollDownRunRef.current = 0;
      scrollUpRunRef.current = 0;
      return;
    }
    if (delta > 0) {
      scrollDownRunRef.current += delta;
      scrollUpRunRef.current = 0;
    } else if (delta < 0) {
      scrollUpRunRef.current -= delta;
      scrollDownRunRef.current = 0;
    }
    // Words held selected in an answer: nothing folds or tucks while they are
    // ("Ask about this" waits at the top of the composer).
    if (selectionLiveRef.current) scrollDownRunRef.current = 0;
    if (scrollDownRunRef.current > TUCK_DOWN_PX && top > TUCK_DOWN_PX) {
      collapseChrome();
      if (top > 24 && maxTop > TUCK_MIN_OVERFLOW) setTuck(true);
    }
    if (scrollUpRunRef.current > UNTUCK_UP_PX) setTuck(false);
    armChromeIdle();
  };

  // ------------------------------------------- "Ask about this" (utils/quote.js)
  // Words selected in a finished answer offer "Ask about this" at the top of
  // the composer, never over the words. A tap puts them in a quote above the
  // box; the question goes with them, or an empty box asks "Tell me more
  // about this." Only while this view is on screen and the switch is on (a
  // team browser always). Not while an answer is coming, when the box can't
  // take focus: the selection is read again when it ends.

  /** The offer, set only when its words change; announced as it appears, never while the handles move. */
  const offerQuote = useCallback((next) => {
    const prev = askOfferRef.current;
    if (prev === next || (prev && next && prev.text === next.text && prev.from === next.from)) return;
    askOfferRef.current = next;
    setAskOffer(next);
    if (next && !prev) setAnnounce(ASK_OFFERED);
    else if (!next) setAnnounce((said) => (said === ASK_OFFERED ? '' : said));
  }, []);

  const askOn = useMemo(() => askAboutOn(publicConfig), [publicConfig]);
  useEffect(() => {
    if (!askOn) {
      selectionLiveRef.current = false;
      readSelectionRef.current = null;
      offerQuote(null);
      return undefined;
    }
    let settleTimer = 0;
    let lingerTimer = 0;
    const read = () => {
      clearTimeout(settleTimer);
      const sel = window.getSelection ? window.getSelection() : null;
      const quote = isShown() ? quoteFromSelection(sel, scrollRef.current) : null;
      selectionLiveRef.current = Boolean(quote);
      if (quote && !loadingRef.current && !boxGoneRef.current) {
        clearTimeout(lingerTimer);
        lingerTimer = 0;
        offerQuote(quote);
      } else if (askOfferRef.current && !lingerTimer) {
        lingerTimer = setTimeout(() => { lingerTimer = 0; offerQuote(null); }, ASK_LINGER_MS);
      }
    };
    // A finger's selection is offered once it holds still; a mouse's at once,
    // as the button comes up (a pointerup is the end of a drag or a double-click).
    const onSelectionChange = () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(read, ASK_SETTLE_MS);
    };
    const onPointerUp = (e) => { if (e.pointerType === 'mouse') read(); };
    document.addEventListener('selectionchange', onSelectionChange);
    document.addEventListener('pointerup', onPointerUp, true);
    readSelectionRef.current = read;
    return () => {
      clearTimeout(settleTimer);
      clearTimeout(lingerTimer);
      document.removeEventListener('selectionchange', onSelectionChange);
      document.removeEventListener('pointerup', onPointerUp, true);
      readSelectionRef.current = null;
    };
  }, [askOn, offerQuote]); // eslint-disable-line react-hooks/exhaustive-deps

  // An answer on its way hides the offer, and so does a card in the box's
  // place; when it ends (or the box is back), a selection made meanwhile in
  // an earlier answer is offered.
  useEffect(() => {
    if (loading || boxGone) offerQuote(null);
    else if (readSelectionRef.current) readSelectionRef.current();
  }, [loading, boxGone, offerQuote]);

  // What moved last, for the two rules that depend on it: after a touch the
  // view keeps room for the selection's end handle, and Remove gives a touch
  // screen's box the focus only if it had it.
  const notePointer = (e) => {
    lastInputRef.current = e.pointerType === 'mouse' ? 'mouse' : 'touch';
    boxFocusedAtPressRef.current = document.activeElement === textareaRef.current;
  };
  const noteKey = () => { lastInputRef.current = 'key'; };

  // The offer row makes the composer taller, and the conversation's view that
  // much shorter: words selected on its last line would slip under the box,
  // right where "Ask about this" now sits, and a phone's handles and Copy menu
  // with them. They are brought back in the same frame. After a touch, with
  // room for the end handle that hangs below the words (24 px), so it never
  // sits on the offer. The offer comes once a finger's selection has held
  // still, so this lands between drags; the one case it can't tell apart, a
  // finger resting mid-drag on the bottom line, is on the real-device list.
  const offered = Boolean(askOffer);
  useLayoutEffect(() => {
    if (!offered) return;
    const scroller = scrollRef.current;
    const row = rootRef.current && rootRef.current.querySelector('.cv-ask-offer-row');
    const sel = window.getSelection ? window.getSelection() : null;
    if (!scroller || !row || !sel || sel.isCollapsed || !sel.rangeCount) return;
    const rects = sel.getRangeAt(0).getClientRects();
    let bottom = -Infinity;
    for (let i = 0; i < rects.length; i++) bottom = Math.max(bottom, rects[i].bottom);
    const handle = lastInputRef.current === 'touch' ? 24 : 0;
    const hidden = bottom + handle - scroller.getBoundingClientRect().bottom;
    // Only what the row itself covered: a selection that ran on below the
    // view before the offer came is the visitor's to scroll to.
    if (hidden > 0 && hidden <= row.offsetHeight + 8 + handle) {
      userScrollUntilRef.current = 0; // an app-made scroll never tucks or folds anything
      scroller.scrollTop += Math.ceil(hidden) + 8;
    }
  }, [offered]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * The offer's tap, in this order: the words let go (a click doesn't clear
   * them, and the offer would come back beside the quote); the box focused
   * inside the tap itself (the only way iOS raises the keyboard); then the
   * quote, the offer gone, the toolbar back, and one announcement.
   */
  const takeOffer = () => {
    const quote = askOfferRef.current;
    if (!quote) return;
    try { window.getSelection().removeAllRanges(); } catch (e) { /* nothing to let go */ }
    selectionLiveRef.current = false;
    const box = textareaRef.current;
    if (box && !box.disabled) box.focus({ preventScroll: true });
    setQuoteDraft(quote);
    offerQuote(null);
    expandChrome();
    setAnnounce(QUOTE_ADDED);
  };

  /**
   * Remove: the quote goes, and so does the button that was pressed. The box
   * takes the focus with a mouse or a keyboard (it would drop to the page),
   * and on a touch screen only if it had it, so the keyboard never comes up
   * for nothing.
   */
  const removeQuote = () => {
    setQuoteDraft(null);
    setAnnounce(QUOTE_REMOVED);
    const box = textareaRef.current;
    if (box && !box.disabled && (lastInputRef.current !== 'touch' || boxFocusedAtPressRef.current)) {
      box.focus({ preventScroll: true });
    }
  };

  // ------------------------------------------------------------ helpers

  const adjustTextareaHeight = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  };

  const chooseTier = (next) => {
    setTier(next);
    try { localStorage.setItem(TIER_KEY, next); } catch (e) { /* ignore */ }
  };

  const findQuestionIndexFor = (assistantMessage) => {
    const idx = messages.indexOf(assistantMessage);
    for (let i = idx - 1; i >= 0; i--) {
      if (messages[i].role === 'user' && !messages[i].local) return i;
    }
    return -1;
  };

  // ---------------------------------------------------------- the turn

  const sendMessage = async (userMessage, options = {}) => {
    const text = (userMessage || '').trim();
    if (!text || loading) return;
    // Nothing is asked while the AI notice is hidden (the first view's reveal,
    // REVEAL): it comes in at once, and the question goes three frames later,
    // once a frame with it has been painted. Should the browser still draw the
    // notice see-through in the first of them (noticeIn), it is made visible
    // before that frame is painted. A question tapped as everything comes in
    // is asked a moment later; one asked twice meanwhile is asked once.
    if (!noticeShownRef.current) {
      if (heldSendRef.current) return;
      heldSendRef.current = true;
      flushSync(() => setReveal(null));
      let sent = false;
      const send = () => {
        if (sent) return;
        sent = true;
        heldSendRef.current = false;
        noticeShownRef.current = true;
        sendMessageRef.current(userMessage, options);
      };
      requestAnimationFrame(() => {
        noticeIn({ force: true });
        requestAnimationFrame(() => requestAnimationFrame(send));
      });
      // A tab that stopped painting still asks it (the notice is in either way).
      setTimeout(() => {
        noticeIn({ force: true });
        send();
      }, 1000);
      return;
    }
    if (!user?.uid) {
      pushLocalNotice('Your session expired. Please refresh the page.');
      return;
    }

    const tierUsed = options.tier || tier;
    const rerun = Boolean(options.rerun);
    // That one was asked, never what it said; an ad's question is asked for them (utils/analytics.js).
    trackAsk({ mode, tier: tierUsed, rerun, landing: Boolean(options.landing) });
    // Meta hears that this visit asked its own first question, never what (utils/metaPixel.js);
    // Front Door, that a visit it counted asked one, and one in its own words (utils/experiments.js).
    if (!rerun && !options.landing) trackFirstQuestion();
    if (!rerun && !options.landing) noteOwnQuestion({ typed: !options.starter });
    const gen = ++genRef.current;
    const controller = new AbortController();
    abortRef.current = controller;
    const requestId = newChatId().replace(/-/g, '').slice(0, 16);
    requestIdRef.current = requestId;

    // If the previous answer was just abandoned, let the server release the
    // lock first (fast; capped) so this send isn't refused as "one at a time".
    if (pendingCancelRef.current) {
      await Promise.race([pendingCancelRef.current, new Promise((r) => setTimeout(r, 1500))]);
      if (gen !== genRef.current) return;
    }

    if (voiceRepliesRef.current) getPlayer().unlock();
    // Why this answer is read aloud (the counts): Read aloud on by default, or switched on.
    const readingSrc = voiceSrc();
    // A reading still going ends here: a question was sent while Rob was speaking.
    let speech = voiceRepliesRef.current ? getPlayer().beginStream({ src: readingSrc, rid: requestId, supersede: 'ask' }) : null;
    // The player's generation moves on when anyone stops it or plays something else.
    let speechGen = speech ? getPlayer().gen : 0;
    // The answer's grant (the done event's, else the last chunk's): kept on the answer so
    // Listen can send it later.
    let lastGrant = null;
    if (speech) setSpeakingKey('live');
    // A crisis turn is spoken gently (utils/voice.js); its safety event comes first.
    let crisisTurn = false;
    // This question's place in the person's window, from its metadata, and
    // whether it carries the support line. The line waits for the first
    // event after the safety event's slot, so a crisis turn never shows it.
    // givingQuiet: the server says no asks for this person (a safety hold,
    // or a crisis in this conversation's history).
    let turnWindow = { count: null, resetAt: null };
    let turnQuiet = false;
    let supportPending = false;
    let supportShown = false;
    // The questions-left numbers before this turn's metadata, for a turn the
    // server gives back (GIVEN_BACK); undefined until the metadata arrives.
    let quotaBeforeTurn;

    streamingRef.current = { active: true, text: '', tier: tierUsed, sources: [], ground: null };
    // "Go deeper" re-asks a question already on screen: a half-typed draft
    // and a quote waiting above the box stay. Every other send empties both.
    if (!rerun) {
      setInputMessage('');
      setQuoteDraft(null);
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
    }
    setLoading(true);
    setActiveTier(tierUsed);
    setStreamingText('');
    setThinking('');
    setLiveSources([]);
    setLiveGround(null);
    setLiveSupport(false);
    setLimitReached(null);
    setQuotaJoin(false);
    setStreamStatus({ phase: 'opening', label: mode === 'archive' ? 'Opening the archive' : 'Reading the sermons' });
    // More's full answer says what it is from its first frame (Go deeper's label stays as it was).
    setStreamLabel(options.more ? 'the full answer' : '');
    setExpected(expectedLatency(tierUsed, mode, null));
    const t0 = Date.now();
    setStartedAt(t0);

    // A question the email gate held is already on screen: it is asked again
    // where it stands, not added a second time.
    const existing = !rerun && options.existingKey
      ? messagesRef.current.find((m) => m.role === 'user' && m.timestamp === options.existingKey) || null
      : null;
    // The words this question asks about, if any (utils/quote.js): from the
    // box's quote, a held question's, or the question "Go deeper" re-asks.
    const quote = cleanQuote(options.quote || (existing && existing.quote));
    const newUserMessage = existing || {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
      ...(options.landing ? { landing: options.landing } : {}),
      ...(quote ? { quote } : {}),
    };
    setAnnounce('AI Rob is answering.');
    // History is captured before appending; for an expert re-ask we stop
    // before the original question so the model answers fresh, and a held
    // question's history is what came before it. More goes on through the
    // short answer (historyThrough): its full answer picks up from it.
    const priorMessages = rerun && options.questionIndex != null
      ? messages.slice(0, options.historyThrough != null ? options.historyThrough : options.questionIndex)
      : existing ? messagesRef.current.slice(0, messagesRef.current.indexOf(existing)) : messages;
    const historyForRequest = buildHistory(priorMessages, rerun ? null : compaction);
    // The chapters of Genius the last answer read, when the server said
    // (ground, groundClient): the server carries a follow-up on to a chapter
    // it read, never on to one it only named. The last answer is the last
    // real one: a donation card or the guest nudge after it is not an answer.
    const lastAnswer = [...priorMessages].reverse().find((m) => m.role === 'assistant' && isRealMessage(m));
    const grounded = lastAnswer && lastAnswer.ground && Array.isArray(lastAnswer.ground.book) ? lastAnswer.ground.book : null;
    // First Answer (utils/experiments.js): the first question of a conversation carries what the
    // server needs to place it (the visitor id, or a team member's own pick); a follow-up in a
    // conversation this tab enrolled is that first answer's going on, counted once. The record is
    // the tab's, by conversation, so a Stop that dropped the first answer, or a reload, loses nothing.
    const firstOfConversation = !rerun && !priorMessages.some((m) => m.role === 'user' && !m.local);
    const faFields = firstAnswerFields({ mode, first: firstOfConversation });
    if (!rerun && !options.landing && !firstOfConversation) wentOn(currentChatId, 'fup');
    // What the answer's metadata says about it (First Answer): null for most; and whether this
    // answer's first words enrolled the conversation in this tab (its end starts the leaving watch).
    let turnFa = null;
    let enrolledNow = false;
    // Park the view once: the question at the top of the screen (a re-ask:
    // the new answer's first line), the answer inking in beneath it. Nothing
    // follows the stream after this.
    scrollOnLoadRef.current = false;
    revealEndRef.current = false;
    anchorRef.current = rerun ? { stream: true } : { key: newUserMessage.timestamp, question: true };
    pendingAnchorRef.current = true;
    scrolledSinceAskRef.current = false;
    if (!rerun && !existing) setMessages((prev) => [...prev, newUserMessage]);

    let activeChatId = currentChatId;
    if (!activeChatId) {
      activeChatId = newChatId();
      setCurrentChatId(activeChatId);
    }

    let localSources = [];
    let localGround = null;
    let firstTokenAt = null;
    try {
      const result = await streamChat(endpoint, {
        message: text,
        chatId: activeChatId,
        // The team keeps every conversation (functions/conversations.js): one
        // id per conversation (it lives in the draft, so a reload continues
        // it, and New conversation starts another), and where it came from.
        conversationId: activeChatId,
        ...conversationContext(),
        requestId,
        history: historyForRequest,
        historySummary: rerun ? '' : (compaction?.summary || ''),
        ...(grounded ? { grounded } : {}),
        persist: saved && !isGuest,
        tier: tierUsed,
        rerun,
        // Its own field, never inside the message: the crisis screen reads only their words.
        ...(quote ? { quote } : {}),
        // First Answer: the visitor id or the team's pick (the first question of a conversation
        // only), and More, which asks the same question for the full answer, naming the short
        // answer it is the rest of (the first More under it is not another question).
        ...faFields,
        ...(options.more ? { more: true, ...(options.moreOf ? { moreOf: options.moreOf } : {}) } : {}),
        ...(extraBody || {}),
      }, {
        signal: controller.signal,
        onEvent: (ev) => {
          if (gen !== genRef.current) return;
          if (!ev.metadata && !ev.safety && supportPending) {
            // Past the safety event's slot: a crisis turn has said so by now.
            supportPending = false;
            if (!crisisTurn) {
              supportShown = true;
              setLiveSupport(true);
              trackGoal('give_line_shown');
            }
          }
          if (ev.metadata) {
            const m = ev.metadata;
            quotaBeforeTurn = quotaRef.current;
            // null = no limit (owners/admins): clear any count left over from before sign-in.
            setQuota(quotaFromMetadata(m));
            applyPublicConfig(m); // the server's give link, numbers and sheet win
            turnWindow = { count: m.windowCount, resetAt: m.resetAt };
            if (m.givingQuiet === true) {
              turnQuiet = true;
              holdGiving();
            }
            // Only while the asks are neither resting nor held on this device (utils/giving.js).
            // Never on More: it is the rest of an answer, not another question (its window count
            // is the short answer's, so the line would come twice).
            supportPending = !options.more && !turnQuiet && getGivingState().ask === 'ask'
              && isSupportTurn(m.windowCount) && !isDonationTurn(m.windowCount, m.resetAt);
            setExpected(expectedLatency(m.tier || tierUsed, mode, m.expected));
            if (m.tier) setActiveTier(m.tier);
            // First Answer: { k, arm, short } for an answer in the test, { short, team } for a team pick.
            turnFa = readFirstAnswer(m.fa);
          } else if (ev.safety) {
            // Arrives before the first word; drawn under this turn's answer (utils/crisisCards.js).
            setMessages((prev) => [...prev, { role: 'assistant', local: true, safetyCard: ev.safety.category, turn: requestId, content: '', timestamp: new Date().toISOString() }]);
            crisisTurn = true;
            holdGiving(); // no ask anywhere on this device for a day
            if (speech) speech.setCrisis();
          } else if (ev.status) {
            setStreamStatus(ev.status);
          } else if (ev.thinking) {
            setThinking((prev) => (prev + ev.thinking).slice(-600));
          } else if (ev.sources) {
            localSources = ev.sources;
            streamingRef.current.sources = ev.sources;
            setLiveSources(ev.sources);
            if (ev.ground) {
              localGround = ev.ground;
              streamingRef.current.ground = ev.ground;
              setLiveGround(ev.ground);
            }
          } else if (ev.reset) {
            // The words so far were a draft the model replaced after searching
            // (functions/replyStream.js). The answer starts over, and so does
            // Rob's voice, unless it has been stopped since.
            streamingRef.current.text = '';
            fadeRef.current = { html: '', settled: 0, spans: [] };
            setStreamingText('');
            // The grant so far covered the draft; the next chunk's covers what comes after.
            lastGrant = null;
            if (speech && getPlayer().gen === speechGen) {
              speech = getPlayer().beginStream({ crisis: crisisTurn, src: readingSrc, rid: requestId, restart: true });
              speechGen = getPlayer().gen;
            }
          } else if (ev.chunk) {
            if (firstTokenAt === null) {
              firstTokenAt = Date.now();
              // First Answer: its first words are where the two groups part, so this is where a
              // first answer in the test enrols its conversation in this tab (enr). The watch for
              // leaving waits for the answer's end, below, so both groups get the same 15 seconds.
              if (turnFa && turnFa.k && enrollFirstAnswer(activeChatId, turnFa)) enrolledNow = true;
            }
            streamingRef.current.text += ev.chunk;
            // An answer that offers help (988, findhelp.org, a hotline) is a hard
            // season: the support line under it goes the moment the help appears.
            if (supportShown && offersHelp(streamingRef.current.text)) {
              supportShown = false;
              setLiveSupport(false);
            }
            if (typeof ev.g === 'string') lastGrant = ev.g;
            if (speech) speech.feed(ev.chunk, ev.g);
            setStreamStatus((prev) => (prev?.phase === 'answering' ? prev : { phase: 'answering', label: 'Shaping the answer' }));
            setStreamingText((prev) => prev + ev.chunk);
          }
        },
      });
      if (gen !== genRef.current) return;

      const totalMs = Date.now() - t0;
      if (typeof result.g === 'string') lastGrant = result.g;
      // An answer that offered help is a hard season (functions/guards.js
      // hardSeasonIn, the same rule as the server's): no support line under it,
      // no card after it, and the asks hold on this device as for givingQuiet.
      const helpOffered = offersHelp(result.text);
      if (helpOffered) {
        supportShown = false;
        turnQuiet = true;
        holdGiving();
      }
      const aiMessage = {
        role: 'assistant',
        content: result.text,
        timestamp: new Date().toISOString(),
        tier: result.tier || tierUsed,
        model: result.model || null,
        sources: (result.sources && result.sources.length ? result.sources : localSources) || [],
        timing: { totalMs, firstTokenMs: firstTokenAt ? firstTokenAt - t0 : null },
        turn: requestId,
        // What was in front of the model, and the checks' code, when the server says (groundClient).
        ...(result.ground || localGround ? { ground: result.ground || localGround } : {}),
        ...(result.checks ? { checks: result.checks } : {}),
        ...(lastGrant ? { grant: lastGrant } : {}),
        // More's full answer is a Fast answer like any (Go deeper stays under it), labelled as what it is.
        ...(rerun && !options.more ? { rerun: true } : {}),
        ...(options.more ? { more: true } : {}),
        // First Answer: the note asked for this answer short (Short + More). More goes under it only
        // while it is short and offered no help (moreFits); the team's record says so either way.
        ...(turnFa && turnFa.short ? { short: true } : {}),
        ...(options.landing ? { landing: options.landing } : {}),
        ...(supportShown ? { support: true } : {}),
      };
      setAnnounce('AI Rob’s answer is ready.');
      recordLatencySample(aiMessage.tier, mode, totalMs);
      // The finished answer takes the streaming bubble's place without a
      // second entrance, and a parked re-ask stays parked on it.
      settledKeysRef.current.add(aiMessage.timestamp);
      if (anchorRef.current && anchorRef.current.stream) anchorRef.current = { key: aiMessage.timestamp };
      if (aiMessage.short && moreFits(aiMessage.content)) revealMoreRef.current = aiMessage.timestamp;
      // First Answer: the answer is done (the stream's done event), and the 15 seconds for leaving
      // begin here, the same for both groups (utils/experiments.js watchFirstAnswerLeaving). An
      // answer that never finished (Stop, an error) starts no watch.
      if (enrolledNow) {
        if (faWatchRef.current) faWatchRef.current.watch.cancel();
        faWatchRef.current = { cid: activeChatId, watch: watchFirstAnswerLeaving(activeChatId) };
      }
      // The donation card, once a window (utils/giving.js). In a conversation
      // that had a crisis card, or while the asks rest or are held on this
      // device, it is held back, for the rest of the window.
      let givingCard = null;
      if (isDonationTurn(turnWindow.count, turnWindow.resetAt)) {
        markDonationCardHandled(turnWindow.resetAt);
        if (!crisisTurn && !turnQuiet && !messagesRef.current.some((m) => m.safetyCard || (m.role === 'assistant' && !m.local && offersHelp(m.content))) && getGivingState().ask === 'ask') {
          givingCard = {
            role: 'assistant', giving: true, content: '', timestamp: new Date(Date.now() + 1).toISOString(),
            spentUsd: Number.isFinite(result.spentUsd) ? result.spentUsd : null,
            resetAt: Number.isFinite(turnWindow.resetAt) ? turnWindow.resetAt : null,
          };
          trackGoal('give_card_shown');
        }
      }
      setMessages((prev) => {
        const next = [...prev, aiMessage];
        if (givingCard) next.push(givingCard);
        if (aiRenoticeDue()) next.push({ role: 'assistant', local: true, content: AI_RENOTICE_TEXT, timestamp: new Date().toISOString() });
        return next;
      });
      if (speech) speech.end(result.g);
      else if (voiceRepliesRef.current && result.text) {
        // Read aloud was switched on while the answer was arriving: read it whole now.
        speakMessage(aiMessage.timestamp, result.text, { crisis: crisisTurn, src: voiceSrc(), rid: requestId, grant: lastGrant });
      }
      streamingRef.current.active = false;
      if (onAnswerComplete) onAnswerComplete();
      if (typeof document !== 'undefined' && document.hidden) flagTitleUntilSeen();

      // Long conversations: condense older turns so the chat keeps working.
      if (!rerun) {
        const finalMessages = [...priorMessages, newUserMessage, aiMessage];
        maybeCompact(finalMessages, compaction).then((next) => {
          if (next && gen === genRef.current) setCompaction(next);
        });
      }
    } catch (err) {
      if (err?.name === 'AbortError' || gen !== genRef.current) return;
      // No answer is coming: release the parked question so the notice below
      // it settles at the end of the conversation instead of above blank space.
      anchorRef.current = null;
      pendingAnchorRef.current = false;
      // No answer is coming, so the reading of it ends too.
      if (speech && getPlayer().gen === speechGen) getPlayer().stop('error');
      // A crisis message that a limit turned away, or that failed, still
      // brings its card (988, 741741, 911, findhelp.org), unless this turn's
      // safety event already did (functions/chat.js).
      if (err?.safetyCategory && !crisisTurn) {
        crisisTurn = true;
        setMessages((prev) => [...prev, { role: 'assistant', local: true, safetyCard: err.safetyCategory, turn: requestId, content: '', timestamp: new Date().toISOString() }]);
        holdGiving();
      }
      // The server gave the question back: so does the questions-left line.
      if (quotaBeforeTurn !== undefined && GIVEN_BACK.has(err?.code)) setQuota(quotaBeforeTurn);
      if (err?.code === 'email_gate') {
        // A guest's free questions for this window are used. The question
        // never reached the model: it waits on screen for an email (or goes
        // back into the box, releaseGate).
        setEmailGate({
          question: text,
          key: rerun ? null : newUserMessage.timestamp,
          resetAt: err.resetAt || null,
          options: {
            tier: tierUsed,
            ...(rerun ? { rerun: true, questionIndex: options.questionIndex } : {}),
            ...(options.more ? { more: true, ...(options.historyThrough != null ? { historyThrough: options.historyThrough } : {}), ...(options.moreOf ? { moreOf: options.moreOf } : {}) } : {}),
            ...(options.starter ? { starter: true } : {}),
            ...(options.landing ? { landing: options.landing } : {}),
            ...(quote ? { quote } : {}),
          },
        });
        setQuota((q) => (q ? { ...q, remaining: 0 } : q));
      } else if (err?.code === 'window_limit' || err?.code === 'daily_limit') {
        // daily_limit: a server from before the windows, while a deploy rolls out.
        // An account is offered only if it would give more questions: not to a
        // guest who gave an email (an account is 40 too), nor under a hold.
        // Under a safety hold (givingQuiet) the card carries the 988 line;
        // it carries the support line only if the asks were due right now.
        const quiet = err.givingQuiet === true;
        if (quiet) holdGiving();
        setLimitReached({
          resetAt: err.resetAt || null,
          moreWithAccount: err.moreWithAccount === true,
          quiet,
          support: !quiet && getGivingState().ask === 'ask',
        });
        setQuota((q) => (q ? { ...q, remaining: 0 } : q));
      } else {
        if (!err?.safeMessage) reportError(err, { where: `chat.${mode}.send`, tags: { tier: tierUsed } });
        pushLocalNotice(mapChatError(err));
      }
    } finally {
      if (gen === genRef.current) {
        streamingRef.current.active = false;
        abortRef.current = null;
        requestIdRef.current = null;
        setStreamingText('');
        setStreamStatus(null);
        setThinking('');
        setLiveSources([]);
        setLiveGround(null);
        setLiveSupport(false);
        setStartedAt(null);
        setActiveTier(null);
        setLoading(false);
      }
    }
  };
  sendMessageRef.current = sendMessage;

  /** The gate's email went through: the held question is asked now. */
  const onGateJoined = () => {
    const gate = emailGateRef.current;
    emailGateRef.current = null;
    setEmailGate(null);
    setJoinedNote(true);
    setQuota(null); // the next answer brings the new numbers
    if (gate) sendMessage(gate.question, { ...gate.options, existingKey: gate.key });
  };

  const signInInstead = () => {
    releaseGate();
    if (onRequestSignIn) onRequestSignIn();
  };

  const onQuotaJoined = () => {
    setQuotaJoin(false);
    setJoinedNote(true);
    setQuota(null);
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    // With a quote above the box, an empty box asks "Tell me more about this." (utils/quote.js).
    const typed = inputMessage.trim();
    sendMessage(typed || (quoteShown ? defaultAsk(quoteShown) : ''), quoteShown ? { quote: quoteShown } : {});
  };

  /**
   * "Ask". While the mic listens (or is still writing down what was said),
   * it finishes and asks what they said: the next thing anyone does after
   * speaking. With nothing typed yet, the caret goes to the box, where the
   * question goes. Otherwise the form sends what is in the box, or with a
   * quote above an empty box, asks about the quote.
   */
  const askOrFocus = (e) => {
    if (recording || transcribing) {
      e.preventDefault();
      askAfterVoiceRef.current = true;
      if (recording && recorderRef.current) recorderRef.current.stop();
      return;
    }
    if (inputMessage.trim() || quoteShown) return; // the form sends it
    e.preventDefault();
    expandChrome();
    if (textareaRef.current) textareaRef.current.focus();
  };

  /** A tap on the box itself (its edge, the room beside the buttons, the caret before the words). */
  const focusBoxFromRow = (e) => {
    if (e.target !== e.currentTarget) return; // the words, the mic, Ask: they do their own thing
    if (textareaRef.current && !textareaRef.current.disabled) textareaRef.current.focus();
  };

  const handleStop = () => {
    cancelInFlight();
    pushLocalNotice('Stopped. Ask again whenever you like.');
  };

  /** Whether the question at qIndex is the conversation's first (First Answer counts what follows it). */
  const isFirstQuestion = (qIndex) => qIndex !== -1 && qIndex === messages.findIndex((m) => m.role === 'user' && !m.local);

  const handleDeeper = (assistantMessage) => {
    const qIndex = findQuestionIndexFor(assistantMessage);
    if (qIndex === -1) return;
    // First Answer: Go deeper on the first question is counted for its first answer, once.
    if (isFirstQuestion(qIndex)) wentOn(currentChatId, 'deeper');
    // A question about highlighted words is asked again with its own quote.
    const { content, quote } = messages[qIndex];
    sendMessage(content, { tier: 'expert', rerun: true, questionIndex: qIndex, ...(quote ? { quote } : {}) });
  };

  /**
   * First Answer's More (utils/experiments.js): the same question again, for the full answer, at
   * Fast, as a new answer under the short one. It is asked from the conversation through the short
   * answer (historyThrough), so the full answer picks up from it instead of starting over
   * (prompts.MORE_NOTE), and it names the short answer (moreOf): the first More under it is not
   * another question. Counted as More, and as going on.
   */
  const handleMore = (assistantMessage) => {
    const qIndex = findQuestionIndexFor(assistantMessage);
    const at = messages.indexOf(assistantMessage);
    if (qIndex === -1 || at === -1) return;
    wentOn(currentChatId, 'more');
    const { content, quote } = messages[qIndex];
    sendMessage(content, {
      tier: 'fast', rerun: true, more: true, questionIndex: qIndex, historyThrough: at + 1,
      ...(assistantMessage.turn ? { moreOf: assistantMessage.turn } : {}), ...(quote ? { quote } : {}),
    });
  };

  // ------------------------------------------------------------ actions

  /**
   * Sends a verdict on one answer. Everything the team needs to diagnose it
   * travels with it: which sermons it retrieved, which model and tier wrote
   * it, how long it took, and — for "Ask the real Rob" — the surrounding
   * conversation, which the panel says plainly before anyone sends.
   */
  const sendFeedback = async (message, verdict, extra = {}) => {
    const key = message.timestamp;
    if (verdict !== 'flag' && feedbackGiven[key] === verdict) return;
    setFeedbackGiven((prev) => ({ ...prev, [key]: verdict === 'flag' ? 'flag' : verdict }));
    try {
      const qIndex = findQuestionIndexFor(message);
      const answerIndex = messages.indexOf(message);
      // Only words travel to the team, so a question about highlighted words
      // carries them in front of it: "Quoting AI Rob: “…”" (utils/quote.js).
      // Their words are capped first (2,000), so the quote in front never
      // pushes the end of what they asked off (the server keeps 2,600).
      const transcript = messages
        .slice(0, answerIndex + 1)
        .filter(isRealMessage)
        .map((m) => ({
          role: m.role, content: m.role === 'user' ? foldForPeople(m.content, m.quote) : m.content, tier: m.tier || null, timestamp: m.timestamp,
        }));

      const response = await authedFetch('submitFeedback', {
        verdict,
        question: qIndex >= 0 ? foldForPeople(String(messages[qIndex].content || '').slice(0, 2000), messages[qIndex].quote) : '',
        answer: (message.content || '').slice(0, 8000),
        answerId: message.timestamp || null,
        sources: message.sources || [],
        answerMeta: {
          model: message.model || null,
          totalMs: message.timing?.totalMs ?? null,
          firstTokenMs: message.timing?.firstTokenMs ?? null,
          buildId: typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : null,
          // The answer's request id: the team's record of what it searched and read (scripts/turn.js).
          turn: message.turn || null,
          // First Answer: the Short + More note asked for it short (never the group).
          ...(message.short === true ? { short: true } : {}),
        },
        transcript,
        chatId: currentChatId || null,
        videoIds,
        mode,
        tier: message.tier || null,
        ...extra,
      });
      let payload = null;
      try { payload = await response.json(); } catch (e) { /* not json */ }
      // A note the crisis screen read as a crisis brings the card, a refusal too (functions/feedback.js).
      const safety = typeof payload?.safety?.category === 'string' ? payload.safety.category : null;
      if (safety) {
        holdGiving();
        setRobReplies((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), safety } }));
      }
      if (!response.ok) throw new Error(`feedback ${response.status}`);
      // { ok, id, safety?, minor? }: minor, they said they are under 18, so no way to reach them was kept.
      if (payload?.minor === true) setRobReplies((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), minor: true } }));
      // First Answer: sent to the real Rob from an answer to the first question, counted once for
      // it; like Go deeper, that is not leaving.
      if (verdict === 'flag' && isFirstQuestion(qIndex)) wentOn(currentChatId, 'rob');
      return payload || { ok: true };
    } catch (err) {
      reportError(err, { where: 'feedback.submit', tags: { verdict } });
      setFeedbackGiven((prev) => ({ ...prev, [key]: null }));
      return false;
    }
  };

  const copyMessage = async (message) => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopiedKey(message.timestamp);
      setTimeout(() => setCopiedKey(null), 1800);
    } catch (err) { /* clipboard blocked — nothing to do */ }
  };

  const shareMessage = async (message) => {
    const text = `${message.content}\n\n— Ask Pastor Rob · ${PUBLIC_SITE_URL}`;
    try {
      if (navigator.share) await navigator.share({ text });
      else await copyMessage(message);
    } catch (err) { /* user cancelled the share sheet — fine */ }
  };

  const handleSaveChat = async () => {
    if (!currentChatId || messages.filter(isRealMessage).length === 0 || saving) return;
    setSaving(true);
    try {
      await saveChatWithMessages(user.uid, currentChatId, videoIds, messages, chatType);
      setSaved(true);
      if (onChatSaved) onChatSaved(currentChatId);
    } catch (err) {
      reportError(err, { where: 'chat.save' });
      pushLocalNotice("Couldn't save this conversation just now. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const dismissNudge = () => {
    try { sessionStorage.setItem(GUEST_NUDGE_KEY, '1'); } catch (e) { /* ignore */ }
    setMessages((prev) => prev.filter((m) => !m.nudge));
  };

  /**
   * The donation card's "Maybe later": the card goes, the asks rest for the
   * rest of its window (utils/giving.js), and focus moves to the first button
   * under the answer above it rather than being lost with the card. The box
   * takes it only with a mouse or a keyboard: on a touch screen it would
   * raise the keyboard for nothing.
   */
  const putCardAway = (card) => {
    restGiving(restUntilWindowEnd(Date.now(), getGivingState().resetAt || card.resetAt));
    const list = messagesRef.current;
    const above = list.slice(0, Math.max(0, list.indexOf(card))).reverse()
      .find((m) => m.role === 'assistant' && isRealMessage(m) && m.content);
    setMessages((prev) => prev.filter((m) => m !== card));
    setTimeout(() => {
      const scroller = scrollRef.current;
      const answer = above && scroller && scroller.querySelector(`[data-key="${CSS.escape(String(above.timestamp))}"]`);
      const first = answer && answer.querySelector('.cv-actions button');
      if (first) first.focus();
      else if (!isCoarsePointer() && textareaRef.current) textareaRef.current.focus({ preventScroll: true });
    }, 0);
  };

  // Guest nudge after the second real answer (once per session)
  useEffect(() => {
    if (!isGuest || loading) return;
    let shown = false;
    try { shown = sessionStorage.getItem(GUEST_NUDGE_KEY) === '1'; } catch (e) { /* ignore */ }
    if (shown || messages.some((m) => m.nudge)) return;
    const answers = messages.filter((m) => m.role === 'assistant' && isRealMessage(m)).length;
    if (answers === 2) {
      setMessages((prev) => [...prev, { role: 'assistant', nudge: true, content: '', timestamp: new Date().toISOString() }]);
    }
  }, [messages, isGuest, loading]);

  // ---------------------------------------------------- arrival from an ad
  // BRAND_SPEC §11.1 and §9.5: the ad's question types into the box (800 ms
  // at the type-on pace, compressed), parks as the heading, and is asked
  // once: Rob's approved first answer when there is one (no model call),
  // otherwise AI Rob, live. Reduced motion: no typing, the final state only.

  const askLanding = async (q) => {
    // Rob's approved first answer: the bundle's copy at once, else the one the
    // team approved since this build shipped (meta/canonicalAnswers, fetched
    // beside the sign-in: utils/canonical.js), else none, and AI Rob answers
    // live. The question stays in the box until that is known.
    const canonical = await approvedAnswerFor(q);
    setArrivalPhase('asked');
    setInputMessage('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    if (canonical && canonical.text) {
      const at = Date.now();
      const question = { role: 'user', content: q.text, timestamp: new Date(at).toISOString(), landing: q.id };
      const answer = {
        role: 'assistant',
        content: canonical.text,
        timestamp: new Date(at + 1).toISOString(),
        canonical: true,
        landing: q.id,
        sources: canonical.sources || [],
        // What was in front of the model when the answer was drafted, when the
        // record has it: a book citation then renders as it did live.
        ...(canonical.ground ? { ground: canonical.ground } : {}),
        // Approved by someone other than Pastor Rob himself: the label says the team's.
        ...(canonical.byRob === false ? { approvedByTeam: true } : {}),
        turn: `canonical-${q.id}`,
      };
      scrollOnLoadRef.current = false;
      anchorRef.current = { key: question.timestamp, question: true };
      pendingAnchorRef.current = true;
      settledKeysRef.current.add(answer.timestamp);
      if (!currentChatId) setCurrentChatId(newChatId());
      setMessages([question, answer]);
      logArrivalEvent('canonical_answer_shown', { q: q.id });
      return;
    }
    sendMessage(q.text, { landing: q.id });
  };

  useEffect(() => {
    if (!landing || landingHandledRef.current || !user?.uid) return;
    landingHandledRef.current = true;
    if (landing.mode !== 'asking') {
      // A statement or "your turn": the ad's words as the heading, and the
      // box waiting for theirs. Nothing is asked for them.
      setLandingHeader({ id: landing.id, text: landing.text });
      return;
    }
    if (prefersReducedMotion()) {
      askLanding(landing);
      return;
    }
    const text = landing.text;
    const steps = Math.min(text.length, 40);
    let step = 0;
    setArrivalPhase('typing');
    arrivalTimerRef.current = setInterval(() => {
      step += 1;
      setInputMessage(text.slice(0, Math.round((text.length * step) / steps)));
      if (step >= steps) {
        clearInterval(arrivalTimerRef.current);
        arrivalTimerRef.current = setTimeout(() => {
          arrivalTimerRef.current = null;
          askLanding(landing);
        }, 160);
      }
    }, 800 / steps);
  }, [landing, user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    if (arrivalTimerRef.current) {
      clearInterval(arrivalTimerRef.current);
      clearTimeout(arrivalTimerRef.current);
    }
  }, []);

  /** "Edit": the question moves into the box, focused, to be put their own way. */
  const editQuestion = (text) => {
    setInputMessage(text);
    expandChrome();
    setTimeout(() => {
      adjustTextareaHeight();
      const el = textareaRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
    }, 0);
  };

  // ------------------------------------------------- real people (§11.1)

  const humanStepsOn = anyHandoffOn(handoffSwitches());
  const conversationHadCrisis = useMemo(() => messages.some((m) => m.safetyCard), [messages]);
  // A hard season is quiet for giving too: an answer here offered help (988,
  // findhelp.org, a hotline), or a note sent to Rob brought up the card.
  const conversationQuiet = useMemo(
    () => conversationHadCrisis
      || messages.some((m) => m.role === 'assistant' && !m.local && offersHelp(m.content))
      || Object.values(robReplies).some((r) => r && r.safety),
    [conversationHadCrisis, messages, robReplies],
  );

  // The support sheet waits for an answer still arriving (leaving mid-answer
  // can cut it off), and a crisis card or a hard season here quiets every ask in the app.
  const giveReportId = useId();
  const windowEnds = (quota && quota.resetAt) || (limitReached && limitReached.resetAt) || (emailGate && emailGate.resetAt) || null;
  useEffect(() => {
    reportConversation(giveReportId, { answering: loading, crisis: conversationQuiet, resetAt: windowEnds });
  }, [giveReportId, loading, conversationQuiet, windowEnds]);
  useEffect(() => () => forgetConversation(giveReportId), [giveReportId]);

  const landingQuestion = useMemo(() => {
    if (landing) return landing;
    const first = messages.find((m) => m.role === 'user' && m.landing);
    return first ? { id: first.landing } : null;
  }, [landing, messages]);
  const firstLandingAnswer = useMemo(
    () => messages.find((m) => m.role === 'assistant' && m.landing && isRealMessage(m)) || null,
    [messages],
  );
  const lastAnswer = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant' && isRealMessage(messages[i]) && messages[i].content) return messages[i];
    }
    return null;
  }, [messages]);
  const ladderOrder = nextStepOrder(landing || null);

  /**
   * A human step: "Meet one", or a rung of the ladder.
   * TODO(TEAM_APP_SPEC §8.2, Lane B): open <HandoffPanel initialKind={kind}
   * entry={entry} /> here, and log handoff_opened { kind, entry } (BRAND_SPEC
   * §15.3). Until the panel exists a person is reached the way people are
   * today: "Ask the real Rob", under the answer the step belongs to.
   */
  const openHumanStep = (kind, entry, answer = null) => {
    const target = answer || lastAnswer;
    if (!target) return;
    setAskRobFor(target.timestamp);
    setAskRobFocus(true);
    setTimeout(() => {
      const scroller = scrollRef.current;
      const panel = scroller && scroller.querySelector(`[data-key="${target.timestamp}"] .askrob-panel`);
      if (panel) panel.scrollIntoView({ block: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }, 60);
  };

  // The docked door hides while the ladder is on screen.
  useEffect(() => {
    const el = ladderRef.current;
    const scroller = scrollRef.current;
    if (!el || !scroller || typeof IntersectionObserver === 'undefined') { setLadderOnScreen(false); return undefined; }
    const io = new IntersectionObserver((entries) => setLadderOnScreen(entries[entries.length - 1].isIntersecting), { root: scroller });
    io.observe(el);
    return () => io.disconnect();
  }, [firstLandingAnswer, humanStepsOn]);

  // ------------------------------------------- citations: tap, then source
  // A superscript tap scrolls to its source row and outlines it for 1.2 s
  // (BRAND_SPEC §9.5); the row opens the sermon at the moment. Modified
  // clicks (new tab) and answers still streaming keep the plain link.
  const handleConversationClick = (e) => {
    if (openScriptureFrom(e)) return;
    if (openBookFrom(e)) return;
    const link = e.target.closest && e.target.closest('sup.cv-cite a');
    if (!link || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const msg = link.closest('.cv-msg');
    const n = link.textContent.trim();
    const row = msg && msg.querySelector(`.cv-src[data-cite="${n}"]`);
    if (!row) return;
    e.preventDefault();
    row.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    row.focus({ preventScroll: true });
    row.classList.add('is-flash');
    clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => row.classList.remove('is-flash'), 1200);
  };

  // ------------------------------------- Bible references: tap, then passage
  // A reference in a finished answer (utils/scripture.js) opens the passage in
  // a new tab on a tap, a click, a middle-click or Enter. Pressing and holding
  // or dragging still selects its words, so a click that ends one of those
  // opens nothing (isSelectionGesture). It opens through a real link clicked
  // inside the tap, the way sermon links open, because Facebook's and
  // Instagram's own browsers, where the ads run, decide where a tab goes.

  // The press, noted before anything else sees it: where, with what, and
  // whether words in the conversation were selected as it went down.
  // Chrome starts no text selection on a focusable element, focused or not,
  // so for the length of a press the reference is not a tab stop: a drag or a
  // long-press selects its words like any others. The next key, or the
  // lifted pointer once its click is done, gives the tab stop back (below),
  // and the focus too, to the one the keyboard had focused: setting its tab
  // stop aside dropped the focus to the page, which a click on a link never does.
  const noteScripturePress = (e) => {
    const el = e.target.closest && e.target.closest('.cv-scripture');
    if (!el) { scriptureDownRef.current = null; return; }
    const sel = window.getSelection ? window.getSelection() : null;
    const scroller = scrollRef.current;
    scriptureDownRef.current = {
      el, x: e.clientX, y: e.clientY, pointerType: e.pointerType,
      hadSelection: Boolean(sel && !sel.isCollapsed && sel.rangeCount && scroller && sel.getRangeAt(0).intersectsNode(scroller)),
    };
    if (el.getAttribute('tabindex') === '0') {
      if (el === document.activeElement) refocusRef.current = el;
      el.removeAttribute('tabindex');
      untabbedRef.current.add(el);
    }
  };

  // Before a key does anything (Tab included), and once a pointer is lifted
  // and its click is done, every reference is a tab stop again, and the one
  // the keyboard had focused has the focus back, unless something else has
  // taken it since.
  useEffect(() => {
    const restore = () => {
      untabbedRef.current.forEach((el) => el.setAttribute('tabindex', '0'));
      untabbedRef.current.clear();
      const focused = refocusRef.current;
      refocusRef.current = null;
      const active = document.activeElement;
      if (focused && focused.isConnected && (!active || active === document.body)) focused.focus({ preventScroll: true });
    };
    const afterPointer = () => { if (untabbedRef.current.size) setTimeout(restore, 0); };
    document.addEventListener('keydown', restore, true);
    document.addEventListener('pointerup', afterPointer, true);
    return () => {
      document.removeEventListener('keydown', restore, true);
      document.removeEventListener('pointerup', afterPointer, true);
    };
  }, []);

  const openScripture = (el) => {
    const url = scriptureUrl(publicConfig, el.dataset);
    if (!url) return;
    // The visitor counts' "Scripture": once for each passage opened, never a
    // click on the reference (it is data-no-track), and never which verse.
    trackScripture();
    // In Facebook's and Instagram's browsers the passage may open in this tab:
    // the draft brings the conversation back, so no unsaved-conversation warning.
    if (inAppBrowser(typeof navigator !== 'undefined' ? navigator.userAgent : '')) noteDeliberateLeave();
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.setAttribute('data-no-track', ''); // counted once, just above
    // Its click stops at the link: Google Analytics' outbound-click listener
    // on the page would note the address, and the address names the verse.
    a.addEventListener('click', (ev) => ev.stopPropagation(), { once: true });
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // True when the click was on a reference (handled, whether it opened or not).
  const openScriptureFrom = (e) => {
    const el = e.target.closest && e.target.closest('.cv-scripture[data-ref]');
    if (!el) return false;
    const down = scriptureDownRef.current;
    scriptureDownRef.current = null;
    if (e.defaultPrevented || !scriptureOn || el.closest('.streaming')) return true;
    const sel = window.getSelection ? window.getSelection() : null;
    const selectionTouches = Boolean(sel && !sel.isCollapsed && sel.rangeCount && sel.getRangeAt(0).intersectsNode(el));
    if (isSelectionGesture({
      down: down && down.el === el ? down : null,
      click: { x: e.clientX, y: e.clientY, detail: e.detail },
      selectionTouches,
    })) return true;
    openScripture(el);
    return true;
  };

  // Enter on a reference is a click, as it is on a link: the same path, counted
  // the same way. Space is not (it scrolls), following the link convention.
  const handleScriptureKey = (e) => {
    if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
    const el = e.target.closest && e.target.closest('.cv-scripture[data-ref]');
    if (!el || el !== e.target) return;
    e.preventDefault();
    el.click();
  };

  // The middle button opens it too, like any link.
  const handleScriptureAux = (e) => {
    if (e.button === 1) openScriptureFrom(e);
  };

  // ------------------------------------- the book: a citation opens the reader
  // A book number, or the chapter's source row, opens the passage it cited
  // (GeniusReader). An answer that came with ground (groundClient) opens on
  // the words the server found in the book word for word, and at the
  // paragraph it said the answer read (data-book-para); otherwise the quote
  // that finds the passage is the answer's own words just before the
  // citation, and a row with no number in the text sends the answer's opening.
  const openBookFrom = (e) => {
    const btn = e.target.closest && e.target.closest('[data-book-chapter]');
    if (!btn || e.defaultPrevented) return false;
    const chapter = Number(btn.getAttribute('data-book-chapter'));
    if (!Number.isInteger(chapter)) return false;
    e.preventDefault();
    const msg = btn.closest('.cv-msg');
    const marker = btn.matches('.cv-cite-book')
      ? btn
      : msg && msg.querySelector(`.cv-markdown .cv-cite-book[data-book-chapter="${chapter}"]`);
    const key = msg && msg.getAttribute('data-key');
    const message = key ? messagesRef.current.find((m) => m.role === 'assistant' && m.timestamp === key) : null;
    const block = marker && (marker.closest('blockquote, p, li') || marker.parentElement);
    const verified = message && message.ground ? verifiedQuoteFor(message.sources, chapter, block ? block.textContent : '') : null;
    const quote = verified || (marker ? quoteNear(marker) : quoteFromAnswer(msg && msg.querySelector('.cv-markdown')));
    const para = parseInt(btn.getAttribute('data-book-para') ?? (marker ? marker.getAttribute('data-book-para') : ''), 10);
    const named = btn.querySelector('.cv-src-title');
    const fallbackTitle = named ? named.textContent : String(btn.getAttribute('title') || '').replace(/^Book\s*·\s*/, '');
    readerOpenerRef.current = { el: btn, key, chapter, row: !btn.matches('.cv-cite-book') };
    setReader({ chapter, quote, ...(Number.isInteger(para) && para >= 0 ? { para } : {}), fallbackTitle });
    return true;
  };

  // What opened the reader, found again if the answer was redrawn meanwhile.
  const readerOpener = () => {
    const o = readerOpenerRef.current;
    if (!o) return null;
    if (o.el && o.el.isConnected) return o.el;
    const scroller = scrollRef.current;
    const msg = scroller && o.key && scroller.querySelector(`[data-key="${CSS.escape(o.key)}"]`);
    return msg && msg.querySelector(`${o.row ? '.cv-src' : '.cv-cite-book'}[data-book-chapter="${o.chapter}"]`);
  };

  const closeReader = () => {
    setReader(null);
    setTimeout(() => { const el = readerOpener(); if (el) el.focus({ preventScroll: true }); }, 0);
  };

  // "Ask about this passage": the words go in the box, and wait there.
  const askAboutReader = (title) => {
    setReader(null);
    setInputMessage(askAboutPassage(title));
    expandChrome();
    setTimeout(() => {
      adjustTextareaHeight();
      const box = textareaRef.current;
      if (box) {
        box.focus({ preventScroll: true });
        box.setSelectionRange(box.value.length, box.value.length);
      } else {
        const el = readerOpener();
        if (el) el.focus({ preventScroll: true });
      }
    }, 0);
  };

  // ------------------------------------------------------------- render

  const realCount = useMemo(() => messages.filter(isRealMessage).length, [messages]);
  // A quote shows, and goes with a question, only in a conversation with
  // answers in it, and only while the switch is on (a team browser always):
  // one kept for a reload whose conversation is gone, or from before the
  // switch went off, waits unseen until the next question empties it. Kept,
  // not dropped, so a switch that turns on a moment later still finds it.
  const quoteShown = askOn && quoteDraft && realCount > 0 ? quoteDraft : null;
  quoteShownRef.current = quoteShown;
  // While a quote is up, whether the box has room for QUOTE_HINT: measured
  // before it is painted, and again when the box changes size (a turned
  // phone, larger text) or the fonts arrive. The box is a new one after the
  // email gate or the limit card gives it back.
  const quoteUp = Boolean(quoteShown);
  useLayoutEffect(() => {
    const box = textareaRef.current;
    if (!quoteUp || !box) return undefined;
    const measure = () => setQuoteHintFits(hintFits(box, QUOTE_HINT));
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (observer) observer.observe(box);
    window.addEventListener('resize', measure);
    let live = true;
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { if (live) measure(); }, () => {});
    return () => {
      live = false;
      if (observer) observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [quoteUp, boxGone]);
  const showTitles = mode !== 'single';
  const firstUserKey = useMemo(() => messages.find((m) => m.role === 'user' && !m.local)?.timestamp || null, [messages]);

  // A crisis card belongs to its turn but is drawn under that turn's answer,
  // and waits under the answer while it is being written (utils/crisisCards.js).
  // The turn being written is the request in flight.
  const { shownMessages, cardsUnderStream } = useMemo(() => placeCrisisCards(messages, {
    isAnswer: (m) => m.role === 'assistant' && isRealMessage(m),
    liveTurn: loading ? requestIdRef.current : null,
  }), [messages, loading]);
  const cardTurns = useMemo(() => crisisTurns(messages), [messages]);

  /**
   * A finished answer, rendered once (answers never change) unless the
   * catalog arrives later: the body, its sources as rows (BRAND_SPEC §11.1),
   * and what it cited. Rob's approved answers set his quotes in the serif;
   * a live answer only for a quote the server found word for word in the
   * book (§6.2: only verified words are serif). An answer that came with
   * ground numbers a book citation only for a chapter it read.
   *
   * The { __html } objects are cached with it: React 19 rewrites an
   * element's innerHTML whenever that object is a new one, even with the same
   * words, which would rebuild every answer on every keystroke (and drop a
   * focused citation or a text selection with it). Bible references key it
   * by on or off only (scriptureOn), never by the config object every
   * answer's metadata applies again, so a new answer redraws none of the others.
   */
  const renderFor = (message) => {
    const cache = renderCacheRef.current;
    const hit = cache.get(message);
    if (hit && hit.videos === videos && hit.single === singleVideoUrl && hit.scripture === scriptureOn) return hit.result;
    const rendered = renderAnswer(message.content, {
      videos,
      sources: message.sources || [],
      singleVideoUrl,
      showTitle: showTitles,
      verbatim: Boolean(message.canonical),
      bookReader: true,
      scripture: scriptureOn,
      // What was in front of the model (groundClient): none for an older answer, which renders as always.
      ground: message.ground || null,
      verifiedQuotes: message.ground ? verifiedQuotesOf(message.sources) : null,
    });
    const result = { ...rendered, htmlProp: { __html: rendered.html }, sourcesProp: { __html: rendered.sourcesHtml } };
    cache.set(message, { videos, single: singleVideoUrl, scripture: scriptureOn, result });
    return result;
  };

  const renderSaveControl = () => {
    if (realCount === 0) return null;
    if (isGuest) {
      return (
        <button type="button" className="cv-pill" onClick={onRequestSignIn} title="Create a free account to keep conversations">
          <Icon name="bookmark" size={16} />
          Sign in to save
        </button>
      );
    }
    if (saved) {
      return (
        <span className="cv-pill saved">
          <Icon name="check" size={16} />
          Saved
        </span>
      );
    }
    return (
      <button type="button" className="cv-pill" onClick={handleSaveChat} disabled={saving}>
        <Icon name="bookmark" size={16} />
        {saving ? 'Saving…' : 'Save chat'}
      </button>
    );
  };

  // The questions-left line: only once three or fewer remain in the window.
  const quotaWarning = quota && quota.remaining != null && !limitReached && !emailGate && quota.remaining <= 3;
  const quotaTime = quota ? resetTimeText(quota.resetAt) : null;
  const quotaText = !quotaWarning ? '' : quota.remaining === 0
    ? `No questions left ${quotaTime ? `until ${quotaTime}` : 'for now'}`
    : `${quota.remaining} question${quota.remaining === 1 ? '' : 's'} left${quotaTime ? ` until ${quotaTime}` : ''}`;
  // A guest whom an email would give more questions can add one from there
  // (not one who already gave one, nor anyone under a hold).
  const quotaCanJoin = quotaWarning && isGuest && quota.moreWithEmail;
  const gateTime = emailGate ? resetTimeText(emailGate.resetAt) : null;
  const limitTime = limitReached ? resetTimeText(limitReached.resetAt) : null;

  const welcomeBlock = welcome || {
    title: 'Ask anything.',
    text: 'AI-assisted answers, drawn from Pastor Rob’s own sermons, with a link to where he said it. A real person from The Net is here when you want one.',
    starters: true,
  };

  // The starters' glimmer (utils/starterGlimmer.js): only on the welcome
  // screen, while the box is empty and unfocused, nothing is on its way and
  // the mic isn't listening; and one thing moves at a time (§9): not while
  // "Ask anything." types and the rest comes in, nor while the caret then
  // blinks in the box. Its pass starts GLIMMER.startMs after the last blink.
  const glimmerActive = realCount === 0 && !loading && !inputFocused && !inputMessage.trim()
    && Boolean(welcomeBlock.starters || welcomeBlock.chips) && !bareWelcome && !emailGate && !limitReached
    && !recording && !transcribing && !welcomeTyping && reveal == null && !caretBlinking;
  const glimmerIndex = useStarterGlimmer({ count: welcomeQuestions.length, active: glimmerActive, pinnedFirst: startersPinned, run: glimmerRun });
  useEffect(() => {
    if (!glimmerActive || glimmerReplays.current >= GLIMMER.idleMs.length) return undefined;
    const t = setTimeout(() => {
      glimmerReplays.current += 1;
      setGlimmerRun((r) => r + 1);
    }, GLIMMER.idleMs[glimmerReplays.current]);
    return () => clearTimeout(t);
  }, [glimmerActive, glimmerRun]);
  const showMoreStarters = () => {
    setWelcomeQuestions((current) => nextStarters(current).map((q) => q.text));
    setStartersPinned(false);
    glimmerReplays.current = 0;
    setGlimmerRun((r) => r + 1);
    setAnnounce('Four more questions to start with.');
  };

  const disclaimer = (
    <p className="cv-disclaimer">
      <span className="cv-disclaimer-line">AI Rob can be wrong; check the linked sermon moments.</span>
      <span className="cv-disclaimer-line">Your conversations are private. <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy</a></span>
    </p>
  );

  const ladderVisible = humanStepsOn && firstLandingAnswer && !conversationHadCrisis;
  // Not while "Ask about this" or its quote has the top of the composer.
  const showDoor = humanStepsOn && !conversationHadCrisis && askRobFor == null && !ladderOnScreen
    && Boolean(lastAnswer) && speechState === 'idle' && !limitReached && !emailGate && !askOffer && !quoteShown;
  // A statement or your-turn landing: a still white bar before the
  // placeholder (decorative), until they start typing (§11.1).
  const showStaticCaret = Boolean(landingHeader) && !inputMessage && !inputFocused && realCount === 0;
  const showWelcome = realCount === 0 && !loading && !landingHeader && arrivalPhase !== 'typing';
  // Nothing asked yet: the question, the box, then questions to try, in the
  // middle of the screen (team review, Oct 2026). The first question sends
  // the box back to the bottom.
  const centered = showWelcome && !welcomeBlock.node && !limitReached && !emailGate;
  // The front page's welcome puts its line under the box (Alexi, Oct 17:
  // "Ask anything." first, with the box), so the first view is the heading
  // right over the box and nothing between them is ever waiting to come in.
  const lineBelowBox = centered && Boolean(welcomeBlock.typeOn);
  // The first view's reveal (REVEAL) is under way: what it still holds back
  // is see-through and can't be tapped, but keeps its place and stays in
  // reach of a screen reader (ChatView.css .holds-*).
  const revealing = reveal != null && centered;
  // The front page's box for a first question (the welcome, a statement or
  // your-turn landing) is the big, plain one (ChatView.css .first-turn), and
  // has no Fast/Deeper choice yet: every Fast answer offers "Go deeper"
  // under it. Not while an ad's question types itself in (nor in the frame
  // before it starts), which keeps the box it always had; not in Focused
  // Study, which keeps its compact box and its choice from the first question.
  const firstTurn = mode === 'archive' && realCount === 0 && !loading && arrivalPhase === 'idle'
    && !limitReached && !emailGate && !(resetNonce === 0 && landing && landing.mode === 'asking');
  // While the mic listens, the box says what to do next: speak, then Ask
  // (which finishes and asks, askOrFocus). Whole in the first question's box
  // at 320 px; the compact box beside the mic and Ask has less room, so a
  // shorter line there, whole from 360 px.
  // With a quote above the box, the box says it can ask about it, or that Ask
  // alone will (utils/quote.js); a box with no room for that gets the first
  // half only (hintFits).
  const placeholderNow = recording
    ? (firstTurn ? 'Speak now, then tap Ask' : 'Speak, then tap Ask')
    : transcribing
      ? 'Transcribing…'
      : quoteShown
        ? (quoteHintFits ? QUOTE_HINT : QUOTE_HINT_SHORT)
        : (realCount > 0 && followupPlaceholder) || placeholder;
  // The default welcome's caret in its your-turn place (§4): at the start of
  // the empty box once "Ask anything." has typed, blinking four times, then
  // still. One live caret: it steps aside while the box has focus (the box's
  // own caret is there then) or words, and while the mic listens. Not on an
  // ad's landing; a new conversation afterwards is the default welcome again.
  const showWelcomeCaret = centered && Boolean(welcomeBlock.typeOn) && !welcomeTyping
    && !(resetNonce === 0 && (landing || prefill)) && !recording && !transcribing;
  const welcomeCaretAside = inputFocused || Boolean(inputMessage);
  // The caret's blinks end with its animation (onAnimationEnd); should a
  // browser never say so (animations switched off some other way), the
  // starters' glimmer is let go a little after the blinks would have ended.
  // The blinks wait for the reveal (the caret is still until then).
  useEffect(() => {
    if (!showWelcomeCaret || !caretBlinking || revealing) return undefined;
    const t = setTimeout(() => setCaretBlinking(false), CARET_BLINK_MS + 600);
    return () => clearTimeout(t);
  }, [showWelcomeCaret, caretBlinking, resetNonce, revealing]);

  // The reveal, part by part: each of REVEAL_MARKS at its time from the first
  // frame lets its parts go (each part's fade starts as its hold goes,
  // ChatView.css), and the last one ends it.
  const revealStart = reveal ? reveal.start : null;
  useEffect(() => {
    if (!revealing || revealStart == null) return undefined;
    const elapsed = performance.now() - revealStart;
    const timers = REVEAL_MARKS.map((ms) => setTimeout(() => {
      setReveal((r) => (r == null || ms >= REVEAL_END_AT ? null : { ...r, at: Math.max(r.at, ms) }));
    }, Math.max(0, ms - elapsed)));
    return () => timers.forEach(clearTimeout);
  }, [revealing, revealStart]);
  // It ends at once, for good, when the box gets words or the welcome goes (a
  // conversation came back, a question was asked), or a crisis hold begins on
  // this device. Focus alone never ends it: the box focused by a script is
  // nobody starting.
  useEffect(() => {
    if (reveal != null && (!centered || inputMessage || giving.ask === 'hold')) setReveal(null);
  }, [reveal, centered, inputMessage, giving.ask]);
  // Once a visit (utils/typeOn): however the first view went (typed out, ended
  // by someone starting or by going out of sight, or never staged: an ad's
  // landing, a link with words, a crisis hold, reduced motion), this tab has
  // had it, so a reload in the same tab never stages it, nor types the
  // heading, again.
  const revealOver = reveal == null;
  useEffect(() => {
    if (revealOver && welcome && welcome.typeOn) markTyped();
  }, [revealOver]); // eslint-disable-line react-hooks/exhaustive-deps
  // A press, while the reveal plays, on controls not showing yet: { controls, at }, or null.
  const unseenPressRef = useRef(null);
  // Anyone starting brings everything in at once, the AI notice included,
  // before what they did can ask anything: a key that types (the first letter
  // goes into the box, below), Tab or Enter, a paste, words reaching the box
  // any other way (dictation, autofill), a tap or click anywhere on the page
  // (the mic and a question among them), a scroll or the wheel, focus landing
  // on something not yet in (Tab from the browser's own bar), or reduced
  // motion or a high-contrast mode switched on. The whole page listens, not
  // just this view: the menu and the AI notice are the shell's.
  // - On the click, never the press: a press would bring a question in under
  //   the finger, and the same tap's click would ask it. The click lands on
  //   whatever is under a part held back, and a press on a part not yet in (a
  //   question, the menu button, a link) never becomes a tap on it, even when
  //   that part comes in under the finger before it lifts: that click is
  //   dropped. So a tap where a question will be asks nothing. A screen
  //   reader's activation has no press, and asks (once the notice is in,
  //   sendMessage).
  // - A press on the box counts at once (endReveal on its row): the box is
  //   there from the first frame, so the tap can only land on it.
  // - A scroll counts once the page has moved: on a short screen it scrolls
  //   to where the questions will be, and a page scrolled to nothing looks
  //   broken. Never a finger's movement alone: a shaky tap would bring a
  //   question in under the finger.
  // - Keys, clicks and pastes are heard first (capture) and change the page at
  //   once, before anything they set off; words that arrive with no key are
  //   heard last, once the box has taken them (drawing the page again before
  //   that would put the box's old words back).
  useEffect(() => {
    if (!revealing) return undefined;
    const now = () => flushSync(() => setReveal(null));
    const soon = () => setReveal(null);
    const onKey = (e) => {
      if (e.key === 'Tab' || e.key === 'Enter'
        || (typeof e.key === 'string' && e.key.length === 1 && e.key !== ' ' && !e.ctrlKey && !e.metaKey && !e.altKey)) now();
    };
    // The controls under a press that weren't showing yet (their click is
    // dropped, below), or none.
    const onPress = (e) => {
      const unseen = [...document.querySelectorAll(TAP_TARGETS)].filter((el) => {
        const b = el.getBoundingClientRect();
        return e.clientX >= b.left && e.clientX <= b.right && e.clientY >= b.top && e.clientY <= b.bottom
          && shownOpacity(el) < UNSEEN_OPACITY;
      });
      unseenPressRef.current = unseen.length ? { controls: unseen, at: performance.now() } : null;
    };
    const onCancel = () => { unseenPressRef.current = null; };
    const onScroll = (e) => {
      const el = e.target === document ? document.scrollingElement : e.target;
      if (el && el.scrollTop > 8) soon();
    };
    const onFocus = (e) => { if (seeThrough(e.target)) soon(); };
    const onMedia = (e) => { if (e.matches) soon(); };
    const heard = { capture: true, passive: true };
    const listeners = [
      ['pointerdown', onPress, heard], ['pointercancel', onCancel, heard], ['click', now, true], ['keydown', onKey, true], ['paste', now, true],
      ['scroll', onScroll, heard], ['wheel', soon, heard], ['focusin', onFocus, true], ['input', soon, false],
    ];
    listeners.forEach(([type, fn, opts]) => document.addEventListener(type, fn, opts));
    const media = typeof window.matchMedia === 'function'
      ? ['(prefers-reduced-motion: reduce)', '(forced-colors: active)'].map((q) => window.matchMedia(q))
      : [];
    media.forEach((m) => (m.addEventListener ? m.addEventListener('change', onMedia) : m.addListener(onMedia)));
    return () => {
      listeners.forEach(([type, fn, opts]) => document.removeEventListener(type, fn, opts));
      media.forEach((m) => (m.removeEventListener ? m.removeEventListener('change', onMedia) : m.removeListener(onMedia)));
    };
  }, [revealing]);
  // A press on a part not yet in is a press on nothing: its click never lands
  // on that part, even when the part came in under the finger before it
  // lifted, or the reveal ended in between (the press focusing a part still
  // fading in ends it, and a phone sends the click after the lift). The press
  // is noted while the reveal plays (above); its click is looked at here, for
  // as long as the welcome is on screen, within a second of the press.
  useEffect(() => {
    if (!centered) return undefined;
    const onClick = (e) => {
      const pressed = unseenPressRef.current;
      unseenPressRef.current = null;
      if (!pressed || performance.now() - pressed.at > 1000) return;
      const control = e.target && e.target.closest ? e.target.closest(TAP_TARGETS) : null;
      if (control && pressed.controls.includes(control)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [centered]);
  // The typing waits until the heading is on screen in a tab someone is
  // looking at. Should it never start while they are, everything comes in
  // after REVEAL_FALLBACK_MS of the page being in front of them.
  const revealWaiting = reveal != null && reveal.start == null;
  useEffect(() => {
    if (!revealWaiting) return undefined;
    let seenMs = 0;
    const timer = setInterval(() => {
      if (document.visibilityState === 'hidden' || !isShown()) return;
      seenMs += 500;
      if (seenMs >= REVEAL_FALLBACK_MS) setReveal((r) => (r && r.start == null ? null : r));
    }, 500);
    return () => clearInterval(timer);
  }, [revealWaiting]); // eslint-disable-line react-hooks/exhaustive-deps
  // Out of sight (another view in front: Contact us opened by its own
  // address, the back button), it ends: the menu and the AI notice belong to
  // the whole app, and never wait on a view nobody can see. Before the first
  // paint, so a page that opens on another view never shows them held.
  useLayoutEffect(() => {
    if (!revealing) return undefined;
    if (!isShown()) {
      setReveal(null);
      return undefined;
    }
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(() => { if (!isShown()) setReveal(null); });
    io.observe(rootRef.current);
    return () => io.disconnect();
  }, [revealing]); // eslint-disable-line react-hooks/exhaustive-deps
  // The fade lengths (REVEAL_STYLE) on the page's root while it plays.
  useLayoutEffect(() => {
    if (!revealing) return undefined;
    const page = document.documentElement;
    Object.entries(REVEAL_STYLE).forEach(([k, v]) => page.style.setProperty(k, v));
    return () => Object.keys(REVEAL_STYLE).forEach((k) => page.style.removeProperty(k));
  }, [revealing]);
  // Nothing is asked until the AI notice has been painted (sendMessage): three
  // frames after the reveal ends. The first runs before the paint that shows
  // it, and makes the notice visible should the browser still draw it
  // see-through (noticeIn); by the third, a frame with it in has been painted.
  useEffect(() => {
    if (revealing) {
      noticeShownRef.current = !NOTICE_STAGED;
      return undefined;
    }
    let next = 0;
    const first = requestAnimationFrame(() => {
      noticeIn({ force: true });
      next = requestAnimationFrame(() => {
        next = requestAnimationFrame(() => { noticeShownRef.current = true; });
      });
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(next);
    };
  }, [revealing]);
  const endReveal = reveal != null ? () => setReveal(null) : undefined;
  // What it still holds back (ChatView.css .holds-*): the line under the box
  // until the caret drops, each question until its turn, the menu and More
  // questions, the AI notice and the fine print (unless the notice is never
  // held back, stages-notice); and Ask's glimmer at its turn.
  const revealHolds = !revealing ? [] : [
    !reveal.dropped && 'line',
    ...REVEAL_QUESTIONS_AT.map((ms, i) => reveal.at < ms && `q${i + 1}`),
    reveal.at < REVEAL_MENU_AT && 'menu',
    NOTICE_STAGED && reveal.at < AI_NOTICE_NOT_BEFORE_MS && 'notice',
  ].filter(Boolean);
  const revealClass = revealing
    ? ` is-revealing${NOTICE_STAGED ? ' stages-notice' : ''}${revealHolds.map((h) => ` holds-${h}`).join('')}${reveal.at >= REVEAL_GLIMMER_AT ? ' is-glimmering' : ''}`
    : '';

  // Front Door (utils/experiments.js): the visit is counted in its group the moment the welcome's
  // questions come in, or would have in Bare: in the first view of a visit at 4 s (or the instant
  // someone starts), and from the first frame for an ad's visitor, a repeat view or reduced
  // motion. Until then the two welcomes are the same, so someone who leaves sooner is in neither
  // group. Only while the welcome is on screen in a tab someone is looking at; once a visit.
  const questionsIn = centered && !(revealing && reveal.at < REVEAL_QUESTIONS_AT[0]);
  useEffect(() => {
    if (!frontDoor || !questionsIn) return undefined;
    const note = () => {
      if (document.visibilityState === 'hidden' || !isShown()) return false;
      noteFrontDoorShown(frontDoor);
      return true;
    };
    if (note()) return undefined;
    const io = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(() => { if (note()) stop(); });
    const onVisible = () => { if (note()) stop(); };
    function stop() {
      document.removeEventListener('visibilitychange', onVisible);
      if (io) io.disconnect();
    }
    document.addEventListener('visibilitychange', onVisible);
    if (io && rootRef.current) io.observe(rootRef.current);
    return stop;
  }, [frontDoor, questionsIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // On a computer the caret in the box says "type here", but nothing has
  // focus until someone clicks. So while nothing is asked yet and nothing has
  // focus, the first letter typed puts the caret in the box and lands there
  // (focused before the key does anything). Not with Ctrl, Cmd or Alt, not
  // mid-composition, not the space bar (it scrolls), and not while a dialog
  // is open. The box is never focused on its own: a screen reader starts at
  // the top of the page, and the starters' glimmer would stop.
  useEffect(() => {
    if (!firstTurn) return undefined;
    const onKey = (e) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey || e.isComposing) return;
      if (typeof e.key !== 'string' || e.key.length !== 1 || e.key === ' ') return;
      const active = document.activeElement;
      if (active && active !== document.body && active !== document.documentElement) return;
      if (document.querySelector('[aria-modal="true"]')) return;
      const box = textareaRef.current;
      if (!box || box.disabled || box.readOnly || !isShown()) return;
      box.focus({ preventScroll: true });
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [firstTurn]); // eslint-disable-line react-hooks/exhaustive-deps

  // On a phone the keyboard rises over the bottom of the screen and the
  // browser brings only the words into view: in the first question's box the
  // mic and Ask sit on a row under them, and could stay behind the keyboard.
  // Once it is up, the whole box comes into view, never past its top edge.
  useEffect(() => {
    if (!inputFocused || !firstTurn || !isCoarsePointer()) return undefined;
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return undefined;
    let timer = 0;
    const reveal = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const row = inputRowRef.current;
        if (!row || document.activeElement !== textareaRef.current) return;
        const r = row.getBoundingClientRect();
        const below = r.bottom + 8 - (vv.offsetTop + vv.height); // hidden under the keyboard
        const room = r.top - 8 - vv.offsetTop; // how far it can go up and keep its top edge
        const by = Math.round(Math.min(below, room));
        if (by <= 0) return;
        // The welcome scrolls itself when it is taller than the screen
        // (.is-empty); otherwise the page (the visual viewport) moves.
        const root = rootRef.current;
        const scrolls = root && /auto|scroll/.test(getComputedStyle(root).overflowY);
        const before = scrolls ? root.scrollTop : 0;
        if (scrolls) root.scrollTop = before + by;
        const moved = scrolls ? root.scrollTop - before : 0;
        if (moved < by) window.scrollBy(0, by - moved);
      }, 150);
    };
    vv.addEventListener('resize', reveal);
    reveal(); // the keyboard may be up already
    return () => {
      clearTimeout(timer);
      vv.removeEventListener('resize', reveal);
    };
  }, [inputFocused, firstTurn]); // eslint-disable-line react-hooks/exhaustive-deps
  // The support line and the donation card never share a conversation with
  // a crisis card, nor a device under the 24-hour hold (utils/giving.js);
  // where a card in the box's place would carry the support line, they get
  // the 988 line instead. Resting asks no more: lines already on screen
  // stay, and the limit card carries one only if it was due when it came.
  const givingHeld = conversationQuiet || giving.ask === 'hold';
  const supportLine = givingHeld ? null : (
    <SupportLine giveUrl={publicConfig.giveUrl} lead={SUPPORT_LEAD} surface="answer" sheet={giveSheetOn} />
  );
  const limitLine = givingHeld || (limitReached && limitReached.quiet)
    ? <CrisisLine />
    : limitReached && limitReached.support
      ? <SupportLine giveUrl={publicConfig.giveUrl} lead={SUPPORT_LEAD} surface="limit" sheet={giveSheetOn} limitTime={limitTime} />
      : null;

  const renderAssistant = (message, index, key) => {
    const isLocal = Boolean(message.local);
    if (isLocal) {
      return (
        <div key={`a-${key}`} data-key={key} className="cv-msg assistant local">
          <p className="cv-note">
            <Icon name="info" size={16} />
            <span>{String(message.content || '').replace(/^\*|\*$/g, '')}</span>
          </p>
        </div>
      );
    }
    const speakingThis = speakingKey === key && speechState !== 'idle';
    const settled = settledKeysRef.current.has(key);
    const { htmlProp, sourcesHtml, sourcesProp, cited } = renderFor(message);
    // With ground, a chapter of the book counts only when it was read (groundClient).
    const shownSources = (message.sources || []).filter((s) => !message.ground || !Array.isArray(message.ground.book)
      || sourceKindOf(s) !== 'book' || message.ground.book.includes(chapterFromTitle(s.title)));
    // An approved first answer says who approved it, truly: Rob only when he did himself.
    const label = message.canonical
      ? (message.approvedByTeam ? 'approved by The Net’s team' : 'Rob approved this answer')
      : message.rerun
        ? 'a deeper answer to the same question'
        : message.more
          ? 'the full answer'
          : sourceSummary(cited.length
          ? countKinds(cited, (c) => c.kind)
          : countKinds(shownSources, sourceKindOf));
    const retrieved = !cited.length && shownSources.length > 0 ? shownSources : null;
    const drewOn = new Set(cited.length ? cited.map((c) => c.kind) : (retrieved || []).map(sourceKindOf));
    const isLanding = message === firstLandingAnswer;
    const video = isLanding ? robVideoFor(landing?.territory) : null;
    return (
      <React.Fragment key={`a-${key}`}>
        <div data-key={key} className={`cv-msg assistant${message.rerun ? ' rerun' : ''}${settled ? ' settled' : ''}${message.canonical ? ' canonical' : ''}`}>
          <AiLabel suffix={label} />
          <div className="cv-answer">
            <div className="cv-markdown" dangerouslySetInnerHTML={htmlProp} />
          </div>
          {message.interrupted && (
            <p className="cv-note" role="note">
              <Icon name="info" size={16} />
              <span>This answer was cut off when the page closed. Ask again and Rob will pick it back up.</span>
            </p>
          )}

          {/* First Answer's More, under a short first answer while it is the latest one: the
              same question again for the full answer (utils/experiments.js). */}
          {message.short && message.content && moreFits(message.content) && message === lastAnswer && !message.interrupted && !loading && !emailGate && !limitReached && (
            <MoreAnswer onMore={() => handleMore(message)} />
          )}

          {message.content && (
            <div className="cv-actions">
              {/* Each accessible name starts with the words on the button, so
                  "click Listen AI voice" works by voice (WCAG 2.5.3). Never in
                  brackets: checkers set bracketed words aside. */}
              <button
                type="button"
                className={`cv-action listen ${speakingThis ? speechState : ''}`}
                onClick={() => speakMessage(key, message.content, {
                  crisis: message.turn ? cardTurns.has(message.turn) : Boolean(shownMessages[index + 1]?.safetyCard),
                  src: 'listen',
                  rid: message.grant ? message.turn : null,
                  grant: message.grant || null,
                })}
                aria-label={speakingThis ? 'Stop reading aloud' : 'Listen, AI voice: AI Rob reads this answer aloud'}
              >
                <Icon name={speakingThis ? 'square' : 'volume-2'} size={16} />
                <span>{speakingThis ? 'Stop' : 'Listen'}</span>
                {!speakingThis && <>{' '}<small>AI voice</small></>}
              </button>
              {navigator.share && (
                <button type="button" className="cv-action share" onClick={() => shareMessage(message)} aria-label="Share answer">
                  <Icon name="share-2" size={16} />
                  <span>Share</span>
                </button>
              )}
              <button type="button" className="cv-action copy" onClick={() => copyMessage(message)} aria-label={copiedKey === key ? 'Copied' : 'Copy answer'}>
                <Icon name={copiedKey === key ? 'check' : 'copy'} size={16} />
                <span>{copiedKey === key ? 'Copied' : 'Copy'}</span>
              </button>
              <button
                type="button"
                className={`cv-action icon ${feedbackGiven[key] === 'up' ? 'chosen' : ''}`}
                onClick={() => sendFeedback(message, 'up')}
                aria-label="Good answer"
                aria-pressed={feedbackGiven[key] === 'up'}
              >
                <Icon name="thumbs-up" size={16} />
              </button>
              <button
                type="button"
                className={`cv-action icon ${feedbackGiven[key] === 'down' ? 'chosen' : ''}`}
                onClick={() => sendFeedback(message, 'down')}
                aria-label="Not quite right"
                aria-pressed={feedbackGiven[key] === 'down'}
              >
                <Icon name="thumbs-down" size={16} />
              </button>
              <button
                type="button"
                className={`cv-action rob ${feedbackGiven[key] === 'flag' ? 'chosen' : ''}`}
                onClick={() => { setAskRobFocus(false); setAskRobFor(askRobFor === key ? null : key); }}
                aria-expanded={askRobFor === key}
                aria-label={feedbackGiven[key] === 'flag' ? 'Sent to Rob' : 'Ask the real Rob: send this answer to Pastor Rob'}
              >
                <Icon name={feedbackGiven[key] === 'flag' ? 'check' : 'flag'} size={16} />
                <span>{feedbackGiven[key] === 'flag' ? 'Sent to Rob' : 'Ask the real Rob'}</span>
              </button>
              {canFixInZed && message.turn && !String(message.turn).startsWith('canonical-') && (
                <a
                  className="cv-action"
                  href={`${ADMIN_SITE_URL}/?zedfix=${encodeURIComponent(message.turn)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Fix in Zed: work this answer through in the team portal"
                >
                  <Icon name="message-square" size={16} />
                  <span>Fix in Zed</span>
                </a>
              )}
            </div>
          )}
          {message.content && (message.timing?.totalMs || (message.tier === 'fast' && !message.rerun && !message.canonical)) && (
            <p className="cv-answer-meta">
              {message.timing?.totalMs && (
                <span>{message.tier === 'expert' ? 'Deeper answer' : 'Fast answer'} · {formatDuration(message.timing.totalMs)}</span>
              )}
              {message.tier === 'fast' && !message.rerun && !message.canonical && (
                <>
                  {message.timing?.totalMs && <span aria-hidden="true"> · </span>}
                  {/* Not while a card has the box's place: it could only be
                      refused, and would take the email gate's held question
                      off the card. */}
                  <button type="button" className="cv-deeper" onClick={() => handleDeeper(message)} disabled={loading || Boolean(emailGate || limitReached)}>
                    Go deeper
                  </button>
                </>
              )}
            </p>
          )}

          {sourcesHtml && <div className="cv-cited" dangerouslySetInnerHTML={sourcesProp} />}
          {retrieved && (
            <div className="cv-sources" aria-label="What this answer drew from"><span className="cv-sources-label">{sourcesHeading(new Set(retrieved.map(sourceKindOf)))}</span><ul className="cv-src-list">{retrieved.map((s, i) => {
              const kind = sourceKindOf(s);
              const date = formatSourceDate(s.publishDate);
              const meta = kind === 'book'
                ? 'Rob’s book'
                : kind === 'blog'
                  ? `${date ? `${date} · ` : ''}post`
                  : `${date}${s.timecode ? `${date ? ' · ' : ''}plays from ${s.timecode}` : ''}`;
              const inner = (
                <>
                  <span className="cv-src-n" aria-hidden="true"><Icon name={kind === 'book' ? 'book-open' : kind === 'blog' ? 'file-text' : 'play'} size={16} /></span>
                  <span className="cv-src-text"><span className="cv-src-title">{s.title}</span><span className="cv-src-meta">{meta}</span></span>
                </>
              );
              const bookChapter = kind === 'book' ? chapterFromTitle(s.title) : null;
              return (
                <li key={`${s.videoId || s.title}-${i}`}>
                  {bookChapter != null ? (
                    <button type="button" className={`cv-src is-book ${kind}`} data-book-chapter={bookChapter} data-track="Genius passage" aria-haspopup="dialog">{inner}</button>
                  ) : s.videoUrl ? (
                    <a className={`cv-src ${kind}`} href={withLeadIn(s.videoUrl)} target="_blank" rel="noopener noreferrer">{inner}</a>
                  ) : (
                    <span className={`cv-src is-static ${kind}`}>{inner}</span>
                  )}
                </li>
              );
            })}</ul></div>
          )}
          {drewOn.has('book') && (
            <p className="cv-promo">
              From Pastor Rob’s book <cite>Genius</cite> ·{' '}
              <a href={BOOK_AMAZON_URL} target="_blank" rel="noopener" data-track="Get the book on Amazon">Get it on Amazon</a>
            </p>
          )}
          {drewOn.has('video') && (
            <p className="cv-promo">
              <a href={THE_NET_YOUTUBE_URL} target="_blank" rel="noopener" data-track="More from The Net on YouTube">More from The Net on YouTube</a>
            </p>
          )}

          {message.support && supportLine}

          {askRobFor === key && (() => {
            // Said they are under 18: no way to reach them is asked for (utils/askRob.js).
            const minor = underEighteenIn(messages) || Boolean(robReplies[key]?.minor);
            return (
              <AskRobPanel
                user={user}
                alreadySent={feedbackGiven[key] === 'flag'}
                focusOnOpen={askRobFocus}
                minor={minor}
                // Abuse came up: the team reaches them only the way they say is safe.
                abuse={abuseCameUpIn(messages)}
                crisis={robReplies[key]?.safety || null}
                onCancel={() => setAskRobFor(null)}
                onSend={async ({ contact, note }) => {
                  const sent = await sendFeedback(message, 'flag', { contact, note });
                  // The card or the under-18 words stay on screen in the panel; otherwise it closes.
                  if (sent && !sent.safety && !sent.minor && !minor) setAskRobFor(null);
                  return Boolean(sent);
                }}
              />
            );
          })()}
        </div>

        {isLanding && video && <RobVideoCard video={video} />}
        {isLanding && ladderVisible && (
          <NextSteps
            order={ladderOrder}
            innerRef={ladderRef}
            onChoose={(kind) => openHumanStep(kind, 'ladder', message)}
          />
        )}
      </React.Fragment>
    );
  };

  const welcomeStarters = (
    <>
      {/* Front Door's Bare: no questions to start with, and no More questions (utils/experiments.js). */}
      {(welcomeBlock.starters || welcomeBlock.chips) && !bareWelcome && (
        <StarterRows questions={welcomeQuestions} onAsk={(q) => sendMessage(q, { starter: true })} disabled={loading} glimmer={glimmerIndex} onMore={showMoreStarters} />
      )}
      {welcomeBlock.examples && (
        <StarterRows questions={welcomeBlock.examples} onAsk={(q) => sendMessage(q, { starter: true })} disabled={loading} label="Questions to try" />
      )}
    </>
  );

  return (
    <div
      ref={rootRef}
      className={`chat-view mode-${mode}${shellTucked ? ' tucked' : ''}${centered ? ' is-empty' : ''}${firstTurn ? ' first-turn' : ''}${emailGate || limitReached || quotaJoin ? ' has-card' : ''}${revealClass}`}
      data-scripture={scriptureLevel === 2 ? 'hint' : undefined}
      onPointerDown={(e) => { notePointer(e); armChromeIdle(); }}
      onKeyDown={() => { noteKey(); armChromeIdle(); }}
    >
      {header && <div className="cv-header-slot" ref={headerSlotRef}>{header}</div>}

      <div className="cv-messages-wrap">
        <div
          className="cv-scroll"
          ref={scrollRef}
          onScroll={handleScroll}
          onTouchStart={noteUserScroll}
          onTouchMove={noteUserScroll}
          onWheel={noteUserScroll}
          onPointerDownCapture={noteScripturePress}
          onKeyDown={handleScriptureKey}
          onAuxClick={handleScriptureAux}
          onClick={handleConversationClick}
        >
          <div className="cv-column">
            <p className="apr-sr-only" role="status" aria-live="polite">{announce}</p>

            {landingHeader && (
              <div className="cv-landing">
                <div className="cv-label">Your turn</div>
                <h1 className="cv-question size-lead">{landingHeader.text}</h1>
                {realCount === 0 && <p className="cv-welcome-text">{welcomeBlock.text}</p>}
              </div>
            )}

            {showWelcome && (
              <div className="cv-welcome">
                {welcomeBlock.node || (
                  <>
                    {welcomeBlock.typeOn ? (
                      <WelcomeTitle
                        text={welcomeBlock.title}
                        animate={welcomeTyping}
                        stop={inputFocused || Boolean(inputMessage) || reveal == null}
                        // The first frame: the rest of the first view is timed from it (REVEAL).
                        onStart={() => setReveal((r) => (r && r.start == null ? { ...r, start: performance.now() } : r))}
                        onDone={() => {
                          setWelcomeTyping(false);
                          // The caret drops into the box, and the line under it comes in. A
                          // typing that never started (very large text, the heading gone)
                          // stages nothing.
                          setReveal((r) => (r == null || r.start == null ? null : { ...r, dropped: true }));
                        }}
                      />
                    ) : (
                      <h1 className="cv-welcome-title">{welcomeBlock.title}</h1>
                    )}
                    {!lineBelowBox && <p className="cv-welcome-text">{welcomeBlock.text}</p>}
                    {!centered && welcomeStarters}
                  </>
                )}
              </div>
            )}

            {shownMessages.map((message, index) => {
              const key = message.timestamp || index;
              if (message.safetyCard) return <CrisisResources key={`s-${key}`} category={message.safetyCard} />;
              if (message.nudge) {
                return (
                  <div key={`n-${key}`} className="cv-card cv-nudge" role="note">
                    <p>
                      <strong>Enjoying the conversation?</strong>{' '}
                      {quota && quota.moreWithAccount === false
                        ? 'A free account keeps your conversations.'
                        : 'A free account keeps your conversations and gives you more questions.'}
                    </p>
                    <div className="cv-card-actions">
                      <button type="button" className="apr-btn" onClick={onRequestSignIn}>Create a free account</button>
                      <button type="button" className="apr-btn apr-btn--text" onClick={dismissNudge}>Not now</button>
                    </div>
                  </div>
                );
              }
              if (message.giving) {
                // Once a window, never beside a crisis card nor under a hold (utils/giving.js).
                if (givingHeld) return null;
                return (
                  <div key={`g-${key}`} className="cv-card cv-nudge giving" role="note">
                    <p>
                      <strong>Support Ask Pastor Rob.</strong> It’s free for everyone, and it always will be.
                      {' '}{costSentence(message.spentUsd, publicConfig.answerCents)} {SUPPORT_LEAD} donate here. A gift of any amount
                      helps us make disciples.
                    </p>
                    <div className="cv-card-actions">
                      <a
                        className="apr-btn apr-btn--quiet"
                        href={publicConfig.giveUrl}
                        target="_blank"
                        rel="noopener"
                        {...(giveSheetOn ? {
                          'aria-haspopup': 'dialog',
                          'data-give-opener': 'card',
                          'data-track': TRACK_OPEN.card,
                          onClick: (e) => openGiveFromClick(e, { surface: 'card' }),
                        } : {})}
                      >
                        Donate
                      </a>
                      <button type="button" className="apr-btn apr-btn--text" onClick={() => putCardAway(message)}>
                        Maybe later
                      </button>
                    </div>
                  </div>
                );
              }
              if (message.role === 'user') {
                return <UserTurn key={`u-${key}`} message={message} first={key === firstUserKey} onEdit={editQuestion} />;
              }
              return renderAssistant(message, index, key);
            })}

            {loading && (
              <div className="cv-msg assistant streaming" ref={streamElRef}>
                <AiLabel suffix={streamLabel} />
                {streamingText ? (
                  <div className="cv-answer">
                    <div className="cv-markdown" ref={streamBodyRef} dangerouslySetInnerHTML={{ __html: streamHtml }} />
                  </div>
                ) : (
                  <ThinkingCard
                    status={streamStatus}
                    tier={activeTier || tier}
                    elapsedMs={elapsedMs}
                    expected={expected}
                    thinking={thinking}
                    sources={liveSources}
                  />
                )}
                {streamingText && (
                  <div className="cv-stream-footer" aria-hidden="true">
                    <span className="cv-timer">{formatDuration(elapsedMs, { decimals: 1 })}</span>
                    {expected?.p50Ms && <span className="cv-expected">usually about {formatDuration(expected.p50Ms)}</span>}
                  </div>
                )}
                {liveSupport && supportLine}
              </div>
            )}
            {cardsUnderStream.map((m) => <CrisisResources key={`s-${m.timestamp}`} category={m.safetyCard} />)}

            <div ref={messagesEndRef} className="cv-end" />
            <div ref={tailSpaceRef} className="cv-tail-space" aria-hidden="true" />
          </div>
        </div>

        {showJump && (
          <button type="button" className="cv-jump" onClick={() => scrollToBottom()} aria-label="Jump to latest">
            <Icon name="chevron-down" size={24} />
          </button>
        )}
      </div>

      {speechState !== 'idle' && (
        <VoiceBar
          state={speechState}
          teamVoice={teamVoice}
          heard={voiceHeard}
          onPause={() => getPlayer().pause()}
          onResume={() => getPlayer().resume()}
          onStop={() => getPlayer().stop('stop')}
          onTypeInstead={() => {
            getPlayer().stop('type');
            if (voiceRepliesRef.current) setVoicePreference(false);
            setTimeout(() => textareaRef.current?.focus(), 0);
          }}
        />
      )}

      {showDoor && (
        <HumanDoor onMeet={() => openHumanStep((landingQuestion && ladderOrder[0]) || 'meet', 'door')} />
      )}

      <div className={`cv-composer${chromeCollapsed ? ' collapsed' : ''}`}>
        <div className="cv-column">
          {/* Above the box, and outside its form: the email form is a form. */}
          {quotaWarning && (
            <p className="cv-quota" role="status">
              {quotaText}
              {quotaCanJoin && !quotaJoin && (
                <> · <button type="button" className="cv-link" onClick={() => setQuotaJoin(true)}>add your email for more</button></>
              )}
            </p>
          )}
          {quotaWarning && conversationHadCrisis && <CrisisLine />}
          {quotaJoin && !emailGate && !limitReached && (
            <div className="cv-quota-join">
              <EmailSignup
                source="card"
                submitLabel="Add my email"
                autoFocus
                onJoined={onQuotaJoined}
                secondary={<button type="button" className="apr-btn apr-btn--text" onClick={() => setQuotaJoin(false)}>Not now</button>}
              />
            </div>
          )}
          {joinedNote && !emailGate && (
            <p className="cv-joined" role="status">
              <Icon name="check" size={16} />
              <span>{JOINED}</span>
            </p>
          )}
          {emailGate ? (
            <>
              {/* A guest's free questions for this window are used: an email
                  keeps them going (utils/emailList.js). Their question waits
                  above, and is asked the moment the email goes through. */}
              <div className="cv-limit cv-gate" role="region" aria-labelledby={gateTitleId}>
                <h2 id={gateTitleId} className="cv-limit-title">Want to keep going?</h2>
                <p className="cv-limit-text">Add your email and keep asking. {PROMISE}</p>
                <EmailSignup
                  source="gate"
                  submitLabel="Keep going"
                  promise={false}
                  autoFocus={!isCoarsePointer()}
                  onJoined={onGateJoined}
                  secondary={<button type="button" className="apr-btn apr-btn--text" onClick={signInInstead}>Sign in instead</button>}
                >
                  {gateTime && <p className="cv-gate-later">Or come back after {gateTime} for more questions.</p>}
                </EmailSignup>
                {conversationHadCrisis && <CrisisLine />}
              </div>
              {disclaimer}
            </>
          ) : limitReached ? (
            <>
              <div className="cv-limit" role="status">
                <h2 className="cv-limit-title">You’ve asked a lot of good questions!</h2>
                <p className="cv-limit-text">{limitTime ? `Your questions refresh at ${limitTime}.` : 'Your questions refresh in a few hours.'}</p>
                {limitLine}
                <div className="cv-limit-actions">
                  {/* TODO(TEAM_APP_SPEC §8.1, Lane B): open the handoff panel on Talk first. */}
                  <a className="apr-btn apr-btn--lg" href="https://www.thenet.org/contact/" target="_blank" rel="noopener noreferrer">Reach a real person at The Net</a>
                  {/* Only where an account would give more questions (limits.liftsFor). */}
                  {isGuest && limitReached.moreWithAccount && (
                    <button type="button" className="apr-btn apr-btn--quiet" onClick={onRequestSignIn}>Create a free account</button>
                  )}
                </div>
              </div>
              {disclaimer}
            </>
          ) : (
            <form onSubmit={handleSubmit} className="cv-form">
              {/* "Ask about this", then the quote it made, above the box and
                  before it in the page, so Shift+Tab from the box reaches
                  them; never beside a quote of the same words. */}
              {askOffer && !(quoteShown && quoteShown.text === askOffer.text) && (
                <AskOffer onAsk={takeOffer} hintId={askHintId} />
              )}
              {quoteShown && <QuoteChip quote={quoteShown} id={quoteChipId} onRemove={removeQuote} />}
              {/* The whole box is the place to type: a tap anywhere in it
                  that isn't a button puts the caret in the words. A tap on
                  it, the mic and Ask included, brings in the rest of the
                  first view's reveal at once (on the press: the same tap
                  can only land on the box). */}
              <div className={`cv-input-row${recording ? ' recording' : ''}`} ref={inputRowRef} onClick={focusBoxFromRow} onPointerDown={endReveal}>
                {showStaticCaret && <span className="cv-static-caret" aria-hidden="true" />}
                {showWelcomeCaret && (
                  <span
                    key={`welcome-caret-${resetNonce}`}
                    className={`cv-static-caret is-welcome${welcomeCaretAside ? ' is-aside' : ''}`}
                    aria-hidden="true"
                    onAnimationStart={(e) => { if (e.animationName === 'cv-caret-blink') setCaretBlinking(true); }}
                    onAnimationEnd={(e) => { if (e.animationName === 'cv-caret-blink') setCaretBlinking(false); }}
                  />
                )}
                <textarea
                  ref={textareaRef}
                  value={inputMessage}
                  onChange={(e) => { setInputMessage(e.target.value); adjustTextareaHeight(); expandChrome(); }}
                  onFocus={() => { setInputFocused(true); expandChrome(); }}
                  onPointerDown={expandChrome} // a tap on an already-focused box brings the toolbar back too
                  onBlur={() => { setInputFocused(false); armChromeIdle(); }}
                  // Enter asks, on a phone too: its keyboard's key says "send"
                  // (enterKeyHint), and someone who texts presses it. Shift+Enter
                  // on a keyboard is a new line.
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                  placeholder={placeholderNow}
                  className="cv-input"
                  disabled={(loading && !inputMessage) || arrivalPhase === 'typing'}
                  readOnly={arrivalPhase === 'typing'}
                  rows={1}
                  // Named as the screen shows it, so "tap Type your question
                  // here" works by voice (WCAG 2.5.3).
                  aria-label={firstTurn ? placeholder : 'Your message'}
                  // A quote above it is read with it.
                  aria-describedby={quoteShown ? quoteChipId : undefined}
                  enterKeyHint="send"
                />
                {micSupported() && (
                  <button
                    type="button"
                    className={`cv-icon-btn mic ${recording ? 'recording' : ''} ${transcribing ? 'busy' : ''}`}
                    onClick={handleMicClick}
                    disabled={loading || transcribing}
                    aria-label={recording ? 'Finish recording' : transcribing ? 'Transcribing' : 'Ask by voice'}
                  >
                    {recording ? <Icon name="square" size={16} /> : transcribing ? <span className="cv-caret-ai" aria-hidden="true" /> : <Icon name="mic" size={20} />}
                  </button>
                )}
                {loading ? (
                  // "Stop", in words, the same size as "Ask" in its place, so
                  // the mic beside it never moves when an answer starts or ends.
                  <button type="button" className="cv-icon-btn send stop cv-word" onClick={handleStop} aria-label="Stop answering">
                    Stop
                  </button>
                ) : (
                  // "Ask", in words (Alexi, Oct 2026: clear to an 84-year-old).
                  // Quiet until there is a question to send, or a quote above
                  // the box (or the mic is listening); a tap on the quiet one
                  // puts the caret in the box instead of doing nothing.
                  // Counted as "Send", as it always has been (utils/analytics.js).
                  <button
                    type="submit"
                    className={`cv-icon-btn send cv-word${inputMessage.trim() || quoteShown ? ' is-ready' : ''}`}
                    disabled={arrivalPhase === 'typing'}
                    aria-disabled={inputMessage.trim() || quoteShown || recording || transcribing ? undefined : 'true'}
                    onClick={askOrFocus}
                    data-track="Send"
                  >
                    Ask
                  </button>
                )}
                {recording && <VoiceLevel stream={micStream} />}
              </div>
              {/* The toolbar folds away on phones while reading (the compact
                  composer section); the wrapper is what animates. Not under
                  the first question's box: a ticked "Fast" there looked more
                  like the button to press than "Ask" (Oct 2026), and there is
                  nothing to save yet. */}
              {!firstTurn && (
                <div className="cv-chrome">
                  <div className="cv-chrome-inner">
                    <div className="cv-toolbar">
                      <div className="cv-tier" role="radiogroup" aria-label="Answer style">
                        <button type="button" role="radio" aria-checked={tier === 'fast'} className={`cv-tier-btn ${tier === 'fast' ? 'on' : ''}`} onClick={() => chooseTier('fast')} title="A quick answer from Rob's sermons (about 6 seconds)">
                          {tier === 'fast' && <Icon name="check" size={16} />}
                          Fast
                        </button>
                        <button type="button" role="radio" aria-checked={tier === 'expert'} className={`cv-tier-btn ${tier === 'expert' ? 'on' : ''}`} onClick={() => chooseTier('expert')} title="A deeper, more thorough answer (about 20 seconds)">
                          {tier === 'expert' && <Icon name="check" size={16} />}
                          Deeper
                        </button>
                      </div>
                      {renderSaveControl()}
                    </div>
                  </div>
                </div>
              )}
              {disclaimer}
            </form>
          )}
        </div>
      </div>

      {centered && (
        <div className="cv-below">
          <div className="cv-column">
            {lineBelowBox && <p className="cv-welcome-text">{welcomeBlock.text}</p>}
            {welcomeStarters}
            {disclaimer}
          </div>
        </div>
      )}

      {reader && (
        <GeniusReader
          chapter={reader.chapter}
          quote={reader.quote}
          para={reader.para}
          fallbackTitle={reader.fallbackTitle}
          onClose={closeReader}
          onAsk={askAboutReader}
        />
      )}
    </div>
  );
}

export default ChatView;
